import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfFont,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfRect,
  PageGen,
  PdfColor,
  PdfPen,
  PdfPoint,
} from '../../pdf/pdf';
import { getPalette, Palette } from '../../pdf/palette';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SmartNode {
  text: string;
  children: SmartNode[];
  level: number;
}

type SmartType =
  | 'list'
  | 'process'
  | 'hierarchy'
  | 'cycle'
  | 'pyramid'
  | 'sandwich'
  | 'staircase'
  | 'matrix'
  | 'onion'
  | 'target'
  | 'spoke-wheel'
  | 'timeline-vertical'
  | 'fishbone'
  | 'flowchart'
  | 'venn'
  | 'comparison'
  | 'double-bubble'
  | 'chevron'
  | 'pyramid-inverted'
  | 'hierarchy-left'
  | 'bow-tie'
  | 'gantt'
  | 'swim-lane'
  | 'treemap'
  | 'decision-tree'
  | 'funnel-wide'
  | 'iceberg'
  | 'scales'
  | 'mountain'
  | 'fan'
  | 'periodic'
  | 'cycle-refined'
  | 'spider-map'
  | 'kwl'
  | 't-chart'
  | 'plus-minus'
  | 'frayer'
  | 'domino'
  | 'conveyor'
  | 'solar-system'
  | 'bridge'
  | 'nested-target'
  | 'gear'
  | 'roadmap'
  | 'waffle'
  | 'stacked-blocks'
  | 'linear-venn';

// ─── Module ───────────────────────────────────────────────────────────────────

export const smartModule: IModule = {
  name: 'smart',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    return {
      type: 'smart',
      attrs,
      content: JSON.stringify(parseSmartList(source)),
    };
  },

  measure(content: ModuleContent, _params: ModuleParams): PdfSize {
    return new PdfSize(
      parseFloat(content.attrs.width) || 300,
      parseFloat(content.attrs.height) || 200
    );
  },

  draw(content: ModuleContent, rect: PdfRect, _align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) return;

    const nodes: SmartNode[] = JSON.parse(content.content || '[]');
    if (nodes.length === 0) return;

    const type = (content.attrs.type || 'list') as SmartType;
    const palette = getPalette(content.attrs.palette || 'soft');
    const {attrs} = content;

    try {
      switch (type) {
        case 'process':
          drawProcess(page, rect, nodes, palette);
          break;
        case 'hierarchy':
          drawHierarchy(page, rect, nodes, palette);
          break;
        case 'cycle':
          drawCycle(page, rect, nodes, palette);
          break;
        case 'pyramid':
          drawPyramid(page, rect, nodes, palette);
          break;
        case 'sandwich':
          drawSandwich(page, rect, nodes, palette);
          break;
        case 'staircase':
          drawStaircase(page, rect, nodes, palette);
          break;
        case 'matrix':
          drawMatrix(page, rect, nodes, palette, attrs);
          break;
        case 'onion':
          drawOnion(page, rect, nodes, palette, false);
          break;
        case 'target':
          drawOnion(page, rect, nodes, palette, true);
          break;
        case 'spoke-wheel':
          drawSpokeWheel(page, rect, nodes, palette);
          break;
        case 'timeline-vertical':
          drawTimelineVertical(page, rect, nodes, palette);
          break;
        case 'fishbone':
          drawFishbone(page, rect, nodes, palette);
          break;
        case 'flowchart':
          drawFlowchart(page, rect, nodes, palette);
          break;
        case 'venn':
          drawVenn(page, rect, nodes, palette);
          break;
        case 'comparison':
          drawComparison(page, rect, nodes, palette);
          break;
        case 'double-bubble':
          drawDoubleBubble(page, rect, nodes, palette);
          break;
        case 'chevron':
          drawChevron(page, rect, nodes, palette);
          break;
        case 'pyramid-inverted':
          drawPyramidInverted(page, rect, nodes, palette);
          break;
        case 'hierarchy-left':
          drawHierarchyLeft(page, rect, nodes, palette);
          break;
        case 'bow-tie':
          drawBowTie(page, rect, nodes, palette);
          break;
        case 'gantt':
          drawGantt(page, rect, nodes, palette);
          break;
        case 'swim-lane':
          drawSwimLane(page, rect, nodes, palette);
          break;
        case 'treemap':
          drawTreemap(page, rect, nodes, palette);
          break;
        case 'decision-tree':
          drawDecisionTree(page, rect, nodes, palette);
          break;
        case 'funnel-wide':
          drawFunnelWide(page, rect, nodes, palette);
          break;
        case 'iceberg':
          drawIceberg(page, rect, nodes, palette);
          break;
        case 'scales':
          drawScales(page, rect, nodes, palette);
          break;
        case 'mountain':
          drawMountain(page, rect, nodes, palette);
          break;
        case 'fan':
          drawFan(page, rect, nodes, palette);
          break;
        case 'periodic':
          drawPeriodic(page, rect, nodes, palette);
          break;
        case 'cycle-refined':
          drawCycleRefined(page, rect, nodes, palette);
          break;
        case 'spider-map':
          drawSpiderMap(page, rect, nodes, palette);
          break;
        case 'kwl':
          drawKwl(page, rect, nodes, palette, attrs);
          break;
        case 't-chart':
          drawTChart(page, rect, nodes, palette, attrs);
          break;
        case 'plus-minus':
          drawPlusMinus(page, rect, nodes, palette);
          break;
        case 'frayer':
          drawFrayer(page, rect, nodes, palette);
          break;
        case 'domino':
          drawDomino(page, rect, nodes, palette);
          break;
        case 'conveyor':
          drawConveyor(page, rect, nodes, palette);
          break;
        case 'solar-system':
          drawSolarSystem(page, rect, nodes, palette);
          break;
        case 'bridge':
          drawBridge(page, rect, nodes, palette);
          break;
        case 'nested-target':
          drawNestedTarget(page, rect, nodes, palette);
          break;
        case 'gear':
          drawGear(page, rect, nodes, palette);
          break;
        case 'roadmap':
          drawRoadmap(page, rect, nodes, palette);
          break;
        case 'waffle':
          drawWaffle(page, rect, nodes, palette);
          break;
        case 'stacked-blocks':
          drawStackedBlocks(page, rect, nodes, palette);
          break;
        case 'linear-venn':
          drawLinearVenn(page, rect, nodes, palette);
          break;
        default:
          drawList(page, rect, nodes, palette);
          break;
      }
    } catch (e) {
      const font = new PdfFont(PdfFont.FONT_NAME, 9);
      page.drawString(
        `SmartArt error: ${(e as Error).message}`,
        font,
        PdfBrush.Red,
        null,
        rect,
        ETextAlign.CenterLeft
      );
    }
  },
};

// ─── Parser ───────────────────────────────────────────────────────────────────

function parseSmartList(source: string): SmartNode[] {
  const lines = source.trim().split('\n');
  const roots: SmartNode[] = [];
  const stack: { node: SmartNode; indent: number }[] = [];

  for (const line of lines) {
    if (!line.trim()) continue;
    const indent = line.search(/\S/);
    const text = line
      .trim()
      .replace(/^[-*•]\s*/, '')
      .trim();
    if (!text) continue;

    const node: SmartNode = { text, children: [], level: 0 };

    while (stack.length > 0 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }

    if (stack.length === 0) {
      roots.push(node);
    } else {
      stack[stack.length - 1].node.children.push(node);
    }
    node.level = stack.length;
    stack.push({ node, indent });
  }

  return roots;
}

// ─── Color & geometry helpers ─────────────────────────────────────────────────

function lightenColor(hex: string, factor: number): PdfColor {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return PdfColor.fromRgb(
    Math.round(r + (255 - r) * factor),
    Math.round(g + (255 - g) * factor),
    Math.round(b + (255 - b) * factor)
  );
}

function mkBrush(hex: string): PdfBrush {
  return new PdfBrush(PdfColor.fromHex(hex));
}
function mkPen(hex: string, w: number): PdfPen {
  return new PdfPen(PdfColor.fromHex(hex), w);
}

function darkTextBrush(palette: Palette, depth: number): PdfBrush {
  return depth === 0 && palette.textColor === '#FFFFFF' ? PdfBrush.White : PdfBrush.Black;
}

function paletteTextBrush(palette: Palette): PdfBrush {
  return palette.textColor === '#FFFFFF' ? PdfBrush.White : PdfBrush.Black;
}

/** Draw a filled arrowhead pointing from (x1,y1) toward (x2,y2). */
function drawArrowHead(
  page: PageGen,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  colorHex: string,
  size = 6
): void {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const a = 0.42;
  page.drawPolygon(null, mkBrush(colorHex), [
    new PdfPoint(x2, y2),
    new PdfPoint(x2 - size * Math.cos(angle - a), y2 - size * Math.sin(angle - a)),
    new PdfPoint(x2 - size * Math.cos(angle + a), y2 - size * Math.sin(angle + a)),
  ]);
}

// ─── Layout: List ─────────────────────────────────────────────────────────────

function drawList(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  const items: { node: SmartNode; depth: number }[] = [];
  const flatten = (ns: SmartNode[], depth: number) => {
    for (const n of ns) {
      items.push({ node: n, depth });
      flatten(n.children, depth + 1);
    }
  };
  flatten(nodes, 0);
  if (items.length === 0) return;

  const gap = 2;
  const rowH = Math.max((rect.height - gap * (items.length - 1)) / items.length, 10);
  const indentStep = Math.min(rect.width * 0.07, 20);
  const accentW = 5;
  const radius = 2;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const {colors} = palette;

  for (let i = 0; i < items.length; i++) {
    const { node, depth } = items[i];
    const colorHex = colors[depth % colors.length];
    const y = rect.top + i * (rowH + gap);
    const xOffset = depth * indentStep;
    const w = rect.width - xOffset;
    const boxRect = new PdfRect(rect.left + xOffset, y, w, rowH);

    const bgColor = depth === 0 ? PdfColor.fromHex(colorHex) : lightenColor(colorHex, 0.82);
    page.drawRoundedRectangle(null, new PdfBrush(bgColor), boxRect, radius);
    page.drawRoundedRectangle(
      null,
      mkBrush(colorHex),
      new PdfRect(rect.left + xOffset, y, accentW, rowH),
      radius
    );

    const textRect = new PdfRect(rect.left + xOffset + accentW + 4, y, w - accentW - 8, rowH);
    const font = page.adjustFont(textRect.width, [node.text], baseFont);
    page.drawString(
      node.text,
      font,
      darkTextBrush(palette, depth),
      null,
      textRect,
      ETextAlign.CenterLeft
    );
  }
}

// ─── Layout: Process ──────────────────────────────────────────────────────────

function drawProcess(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const hasChildren = nodes.some((n) => n.children.length > 0);
  const childAreaH = hasChildren ? Math.min(rect.height * 0.38, 65) : 0;
  const boxH = rect.height - childAreaH - (hasChildren ? 8 : 0);
  const arrowSlot = rect.width * 0.04;
  const boxW = (rect.width - arrowSlot * Math.max(nodes.length - 1, 0)) / nodes.length;
  const radius = 4;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const boxX = rect.left + i * (boxW + arrowSlot);
    const boxRect = new PdfRect(boxX, rect.top, boxW, boxH);

    page.drawRoundedRectangle(null, mkBrush(colorHex), boxRect, radius);

    const badgeR = Math.max(Math.min(boxW * 0.14, boxH * 0.22, 12), 7);
    const badgeCx = boxX + badgeR + 5;
    const badgeCy = rect.top + badgeR + 5;
    page.drawEllipse(
      null,
      new PdfBrush(PdfColor.fromRgb(255, 255, 255)),
      new PdfRect(badgeCx - badgeR, badgeCy - badgeR, badgeR * 2, badgeR * 2)
    );
    const badgeFont = new PdfFont(PdfFont.FONT_NAME, Math.max(badgeR * 0.95, 6));
    page.drawString(
      `${i + 1}`,
      badgeFont,
      mkBrush(colorHex),
      null,
      new PdfRect(badgeCx - badgeR, badgeCy - badgeR, badgeR * 2, badgeR * 2),
      ETextAlign.Center
    );

    const textTop = rect.top + badgeR * 2 + 10;
    const textRect = new PdfRect(boxX + 4, textTop, boxW - 8, boxH - textTop + rect.top - 6);
    const font = page.adjustFont(textRect.width, [node.text], baseFont);
    page.drawString(node.text, font, paletteTextBrush(palette), null, textRect, ETextAlign.Center);

    if (i < nodes.length - 1) {
      const arrowMidY = rect.top + boxH / 2;
      const ax1 = boxX + boxW + 2;
      const ax2 = boxX + boxW + arrowSlot - 2;
      const tipH = 6;
      const nextColor = colors[(i + 1) % colors.length];
      page.drawLine(
        mkPen(nextColor, 1.5),
        new PdfPoint(ax1, arrowMidY),
        new PdfPoint(ax2 - tipH + 1, arrowMidY)
      );
      drawArrowHead(page, ax1, arrowMidY, ax2, arrowMidY, nextColor, tipH);
    }

    if (node.children.length > 0) {
      const childY = rect.top + boxH + 8;
      const childRowH = Math.max(childAreaH / node.children.length - 1, 9);
      const lightFill = new PdfBrush(lightenColor(colorHex, 0.8));
      const dotBrush = mkBrush(colorHex);
      const dotR = 2.5;
      for (let j = 0; j < node.children.length; j++) {
        const child = node.children[j];
        const cy = childY + j * (childRowH + 1);
        page.drawRoundedRectangle(null, lightFill, new PdfRect(boxX, cy, boxW, childRowH), 2);
        page.drawEllipse(
          null,
          dotBrush,
          new PdfRect(boxX + 5, cy + childRowH / 2 - dotR, dotR * 2, dotR * 2)
        );
        const cTextRect = new PdfRect(
          boxX + 5 + dotR * 2 + 3,
          cy,
          boxW - 5 - dotR * 2 - 5,
          childRowH
        );
        const cf = page.adjustFont(cTextRect.width, [child.text], baseFont);
        page.drawString(child.text, cf, PdfBrush.Black, null, cTextRect, ETextAlign.CenterLeft);
      }
    }
  }
}

// ─── Layout: Hierarchy ────────────────────────────────────────────────────────

function countLeaves(node: SmartNode): number {
  return node.children.length === 0 ? 1 : node.children.reduce((s, c) => s + countLeaves(c), 0);
}

interface HierarchyPos {
  node: SmartNode;
  cx: number;
  cy: number;
  parentCx?: number;
  parentBottomY?: number;
  depth: number;
}

function computeHierarchyPos(
  nodes: SmartNode[],
  xStart: number,
  xEnd: number,
  y: number,
  rowH: number,
  nodeH: number,
  parentCx?: number,
  parentBottomY?: number,
  depth = 0
): HierarchyPos[] {
  const result: HierarchyPos[] = [];
  const totalLeaves = nodes.reduce((s, n) => s + countLeaves(n), 0);
  let cur = xStart;

  for (const n of nodes) {
    const leaves = countLeaves(n);
    const w = ((xEnd - xStart) * leaves) / totalLeaves;
    const nodeCx = cur + w / 2;
    result.push({ node: n, cx: nodeCx, cy: y, parentCx, parentBottomY, depth });
    if (n.children.length > 0) {
      result.push(
        ...computeHierarchyPos(
          n.children,
          cur,
          cur + w,
          y + rowH,
          rowH,
          nodeH,
          nodeCx,
          y + nodeH,
          depth + 1
        )
      );
    }
    cur += w;
  }
  return result;
}

function drawHierarchy(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const linePen = new PdfPen(PdfColor.fromRgb(160, 160, 160), 0.75);

  const getMaxDepth = (ns: SmartNode[], d: number): number =>
    ns.length === 0 ? d : Math.max(...ns.map((n) => getMaxDepth(n.children, d + 1)));
  const maxDepth = getMaxDepth(nodes, 1);

  const rowH = rect.height / maxDepth;
  const nodeH = Math.min(rowH * 0.5, 24);
  const positions = computeHierarchyPos(
    nodes,
    rect.left,
    rect.left + rect.width,
    rect.top,
    rowH,
    nodeH
  );

  for (const pos of positions) {
    if (pos.parentCx !== undefined && pos.parentBottomY !== undefined) {
      const midY = (pos.parentBottomY + pos.cy) / 2;
      page.drawLines(linePen, [
        new PdfPoint(pos.parentCx, pos.parentBottomY),
        new PdfPoint(pos.parentCx, midY),
        new PdfPoint(pos.cx, midY),
        new PdfPoint(pos.cx, pos.cy),
      ]);
    }
  }

  for (const pos of positions) {
    const colorHex = colors[pos.depth % colors.length];
    const totalLeaves = nodes.reduce((s, n) => s + countLeaves(n), 0);
    const nodeW = Math.min(
      (rect.width / totalLeaves) * countLeaves(pos.node) * 0.85,
      rect.width * 0.25,
      80
    );
    const nodeRect = new PdfRect(pos.cx - nodeW / 2, pos.cy, nodeW, nodeH);
    page.drawRoundedRectangle(null, mkBrush(colorHex), nodeRect, 3);
    const tBrush =
      pos.depth === 0 && palette.textColor === '#FFFFFF' ? PdfBrush.White : PdfBrush.Black;
    const font = page.adjustFont(nodeW - 8, [pos.node.text], baseFont);
    page.drawString(
      pos.node.text,
      font,
      tBrush,
      null,
      new PdfRect(pos.cx - nodeW / 2 + 4, pos.cy, nodeW - 8, nodeH),
      ETextAlign.Center
    );
  }
}

// ─── Layout: Cycle ────────────────────────────────────────────────────────────

function drawCycle(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const nodeW = Math.min(rect.width * 0.24, rect.height * 0.24, 75);
  const nodeH = Math.min(nodeW * 0.52, 28);
  const orbitR = Math.min(rect.width, rect.height) / 2 - nodeH * 0.6;
  const startAngle = -Math.PI / 2;

  const nodeCenters = nodes.map((_, i) => ({
    x: cx + Math.cos(startAngle + (2 * Math.PI * i) / nodes.length) * orbitR,
    y: cy + Math.sin(startAngle + (2 * Math.PI * i) / nodes.length) * orbitR,
  }));

  for (let i = 0; i < nodes.length; i++) {
    const from = nodeCenters[i];
    const to = nodeCenters[(i + 1) % nodes.length];
    const nextColor = colors[(i + 1) % colors.length];
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) continue;
    const margin = nodeW * 0.58;
    const p1x = from.x + (dx / len) * margin;
    const p1y = from.y + (dy / len) * margin;
    const p2x = to.x - (dx / len) * margin;
    const p2y = to.y - (dy / len) * margin;

    page.drawLine(mkPen(nextColor, 1.2), new PdfPoint(p1x, p1y), new PdfPoint(p2x, p2y));
    drawArrowHead(page, p1x, p1y, p2x, p2y, nextColor, 7);
  }

  if (nodes.length > 2) {
    const centerR = Math.max(orbitR * 0.12, 6);
    page.drawEllipse(
      mkPen(colors[0], 1),
      new PdfBrush(lightenColor(colors[0], 0.88)),
      new PdfRect(cx - centerR, cy - centerR, centerR * 2, centerR * 2)
    );
  }

  for (let i = 0; i < nodes.length; i++) {
    const colorHex = colors[i % colors.length];
    const { x: ncx, y: ncy } = nodeCenters[i];
    const nodeRect = new PdfRect(ncx - nodeW / 2, ncy - nodeH / 2, nodeW, nodeH);
    page.drawRoundedRectangle(null, mkBrush(colorHex), nodeRect, 4);
    const font = page.adjustFont(nodeW - 8, [nodes[i].text], baseFont);
    page.drawString(
      nodes[i].text,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(ncx - nodeW / 2 + 4, ncy - nodeH / 2, nodeW - 8, nodeH),
      ETextAlign.Center
    );
  }
}

// ─── Layout: Pyramid ─────────────────────────────────────────────────────────

function drawPyramid(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  const levelMap = new Map<number, SmartNode[]>();
  const collect = (ns: SmartNode[], depth: number) => {
    for (const n of ns) {
      if (!levelMap.has(depth)) levelMap.set(depth, []);
      levelMap.get(depth)!.push(n);
      collect(n.children, depth + 1);
    }
  };
  collect(nodes, 0);

  const levels = Array.from(levelMap.keys()).sort((a, b) => a - b);
  if (levels.length === 0) return;

  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const n = levels.length;
  const gap = 2;
  const bandH = (rect.height - gap * (n - 1)) / n;
  const apexFrac = 0.15;

  for (let li = 0; li < n; li++) {
    const colorHex = colors[levels[li] % colors.length];
    const levelNodes = levelMap.get(levels[li])!;
    const yTop = rect.top + li * (bandH + gap);
    const yBot = yTop + bandH;
    const wTop = rect.width * (apexFrac + ((1 - apexFrac) * li) / n);
    const wBot = rect.width * (apexFrac + ((1 - apexFrac) * (li + 1)) / n);
    const xTopLeft = rect.left + (rect.width - wTop) / 2;
    const xBotLeft = rect.left + (rect.width - wBot) / 2;

    page.drawPolygon(null, mkBrush(colorHex), [
      new PdfPoint(xTopLeft, yTop),
      new PdfPoint(xTopLeft + wTop, yTop),
      new PdfPoint(xBotLeft + wBot, yBot),
      new PdfPoint(xBotLeft, yBot),
    ]);

    const label = levelNodes.map((nd) => nd.text).join(' • ');
    const textW = Math.max(wTop, wBot) - 12;
    const font = page.adjustFont(textW, [label], baseFont);
    page.drawString(
      label,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(rect.left + (rect.width - textW) / 2, yTop, textW, bandH),
      ETextAlign.Center
    );
  }
}

// ─── Layout: Sandwich ─────────────────────────────────────────────────────────

function drawSandwich(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const gap = 3;
  const bandH = (rect.height - gap * (n - 1)) / n;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const y = rect.top + i * (bandH + gap);
    page.drawRoundedRectangle(
      null,
      mkBrush(colorHex),
      new PdfRect(rect.left, y, rect.width, bandH),
      3
    );

    const tBrush = paletteTextBrush(palette);

    if (node.children.length === 0) {
      // Label centered
      const font = page.adjustFont(rect.width - 16, [node.text], baseFont);
      page.drawString(
        node.text,
        font,
        tBrush,
        null,
        new PdfRect(rect.left + 8, y, rect.width - 16, bandH),
        ETextAlign.Center
      );
    } else {
      // Label on left third, children as pills on the right
      const labelW = rect.width * 0.32;
      const font = page.adjustFont(labelW - 10, [node.text], baseFont);
      page.drawString(
        node.text,
        font,
        tBrush,
        null,
        new PdfRect(rect.left + 6, y, labelW - 6, bandH),
        ETextAlign.CenterLeft
      );

      const pillArea = rect.width - labelW - 8;
      const pillCount = node.children.length;
      const pillW = (pillArea - (pillCount - 1) * 4) / pillCount;
      const pillH = Math.min(bandH - 8, 20);
      const pillY = y + (bandH - pillH) / 2;
      const lightColor = lightenColor(colorHex, 0.7);

      for (let j = 0; j < pillCount; j++) {
        const px = rect.left + labelW + j * (pillW + 4);
        page.drawRoundedRectangle(
          null,
          new PdfBrush(lightColor),
          new PdfRect(px, pillY, pillW, pillH),
          pillH / 2
        );
        const cf = page.adjustFont(pillW - 8, [node.children[j].text], childFont);
        page.drawString(
          node.children[j].text,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(px + 4, pillY, pillW - 8, pillH),
          ETextAlign.Center
        );
      }
    }
  }
}

// ─── Layout: Staircase ────────────────────────────────────────────────────────

function drawStaircase(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const gap = 2;
  const stepW = (rect.width - gap * (n - 1)) / n;
  const unitH = rect.height / n; // height increment per step
  const linePen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 1.5);

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const x = rect.left + i * (stepW + gap);
    const blockH = (i + 1) * unitH;
    const y = rect.top + rect.height - blockH;

    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(x, y, stepW, blockH), 3);

    // Step number badge at bottom
    const numH = Math.min(unitH * 0.7, 18);
    const numFont = new PdfFont(PdfFont.FONT_NAME, Math.min(numH * 0.65, 11));
    page.drawString(
      `${i + 1}`,
      numFont,
      paletteTextBrush(palette),
      null,
      new PdfRect(x, y + blockH - numH, stepW, numH),
      ETextAlign.Center
    );

    // Label above badge
    const textH = blockH - numH - 6;
    if (textH > 10) {
      const font = page.adjustFont(stepW - 8, [node.text], baseFont);
      page.drawString(
        node.text,
        font,
        paletteTextBrush(palette),
        null,
        new PdfRect(x + 4, y + 4, stepW - 8, textH),
        ETextAlign.Center
      );
    }

    // White separator line at top of the step riser (visual staircase edge)
    if (i > 0) {
      page.drawLine(linePen, new PdfPoint(x, y), new PdfPoint(x, y + unitH));
    }
  }
}

// ─── Layout: Matrix ───────────────────────────────────────────────────────────

function drawMatrix(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette,
  attrs: Record<string, string>
): void {
  if (nodes.length === 0) return;
  const cols = Math.max(parseInt(attrs.cols || '2', 10) || 2, 1);
  const rows = Math.ceil(nodes.length / cols);
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const gap = 4;

  // Axis label areas
  const hasAxisH = Boolean(attrs['label-top'] || attrs['label-bottom']);
  const hasAxisV = Boolean(attrs['label-left'] || attrs['label-right']);
  const axisH = hasAxisH ? 14 : 0;
  const axisV = hasAxisV ? 14 : 0;
  const axisFont = new PdfFont(PdfFont.FONT_NAME, 8);

  const gridLeft = rect.left + (attrs['label-left'] ? axisV : 0);
  const gridTop = rect.top + (attrs['label-top'] ? axisH : 0);
  const gridW = rect.width - (attrs['label-left'] ? axisV : 0) - (attrs['label-right'] ? axisV : 0);
  const gridH =
    rect.height - (attrs['label-top'] ? axisH : 0) - (attrs['label-bottom'] ? axisH : 0);

  const cellW = (gridW - gap * (cols - 1)) / cols;
  const cellH = (gridH - gap * (rows - 1)) / rows;
  const titleH = Math.min(cellH * 0.32, 22);

  for (let i = 0; i < nodes.length; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const x = gridLeft + col * (cellW + gap);
    const y = gridTop + row * (cellH + gap);

    // Cell background (light fill) + colored border
    page.drawRoundedRectangle(
      null,
      new PdfBrush(lightenColor(colorHex, 0.88)),
      new PdfRect(x, y, cellW, cellH),
      4
    );
    page.drawRoundedRectangle(mkPen(colorHex, 1.5), null, new PdfRect(x, y, cellW, cellH), 4);

    // Colored title bar
    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(x, y, cellW, titleH), 4);

    const titleFont = page.adjustFont(cellW - 8, [node.text], baseFont);
    page.drawString(
      node.text,
      titleFont,
      paletteTextBrush(palette),
      null,
      new PdfRect(x + 4, y, cellW - 8, titleH),
      ETextAlign.Center
    );

    // Children as bullet list
    if (node.children.length > 0) {
      const bodyH = cellH - titleH - 4;
      const childRowH = bodyH / node.children.length;
      for (let j = 0; j < node.children.length; j++) {
        const cy = y + titleH + 2 + j * childRowH;
        const cf = page.adjustFont(cellW - 16, [node.children[j].text], childFont);
        page.drawString(
          `• ${node.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(x + 8, cy, cellW - 16, childRowH),
          ETextAlign.CenterLeft
        );
      }
    }
  }

  // Optional axis labels
  const axisLabelFont = new PdfFont(PdfFont.FONT_NAME, 8);
  if (attrs['label-top'])
    page.drawString(
      attrs['label-top'],
      axisLabelFont,
      PdfBrush.Black,
      null,
      new PdfRect(gridLeft, rect.top, gridW, axisH),
      ETextAlign.Center
    );
  if (attrs['label-bottom'])
    page.drawString(
      attrs['label-bottom'],
      axisLabelFont,
      PdfBrush.Black,
      null,
      new PdfRect(gridLeft, rect.top + rect.height - axisH, gridW, axisH),
      ETextAlign.Center
    );
  if (attrs['label-left'])
    page.drawString(
      attrs['label-left'],
      axisLabelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, gridTop, axisV, gridH),
      ETextAlign.Center
    );
  if (attrs['label-right'])
    page.drawString(
      attrs['label-right'],
      axisLabelFont,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left + rect.width - axisV, gridTop, axisV, gridH),
      ETextAlign.Center
    );

  // Divider lines between cells (subtle)
  const divPen = new PdfPen(PdfColor.fromRgb(210, 210, 210), 0.5);
  for (let c = 1; c < cols; c++) {
    const lx = gridLeft + c * (cellW + gap) - gap / 2;
    page.drawLine(divPen, new PdfPoint(lx, gridTop), new PdfPoint(lx, gridTop + gridH));
  }
  for (let r = 1; r < rows; r++) {
    const ly = gridTop + r * (cellH + gap) - gap / 2;
    page.drawLine(divPen, new PdfPoint(gridLeft, ly), new PdfPoint(gridLeft + gridW, ly));
  }
}

// ─── Layout: Onion / Target ───────────────────────────────────────────────────
// onion=false → node[0] is outermost ring (context → core)
// onion=true (target) → node[0] is innermost (highest priority → broad)

function drawOnion(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette,
  isTarget: boolean
): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const maxRx = rect.width / 2 - 2;
  const maxRy = rect.height / 2 - 2;
  const n = nodes.length;

  // For target: reverse drawing order so index 0 ends up in center
  const drawOrder = isTarget
    ? nodes.map((_, i) => n - 1 - i) // draw outermost first, innermost last
    : nodes.map((_, i) => i);

  // Draw ellipses from largest (outermost) to smallest (innermost)
  for (let di = 0; di < n; di++) {
    const nodeIdx = drawOrder[di];
    // Ring fraction: di=0 → full size, di=n-1 → smallest
    const frac = (n - di) / n;
    const rx = maxRx * frac;
    const ry = maxRy * frac;
    const colorHex = colors[nodeIdx % colors.length];
    page.drawEllipse(null, mkBrush(colorHex), new PdfRect(cx - rx, cy - ry, rx * 2, ry * 2));
  }

  // Draw labels: for each ring, text goes in the annular band (between its ellipse and the next inner one)
  const labelPen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 0.5);
  for (let di = 0; di < n; di++) {
    const nodeIdx = drawOrder[di];
    const frac = (n - di) / n;
    const innerFrac = di < n - 1 ? (n - di - 1) / n : 0;
    const rx = maxRx * frac;
    const innerRx = maxRx * innerFrac;
    const ry = maxRy * frac;
    const innerRy = maxRy * innerFrac;

    // Available band: between the outer and inner ellipse
    const bandRx = rx - innerRx;
    const bandRy = ry - innerRy;

    if (bandRx < 4) continue;

    // Place label in the LEFT part of the ring (west side of annulus)
    const labelX = cx - rx + 4;
    const labelW = bandRx - 8;
    const labelH = Math.min(bandRy * 1.4, 14);
    const labelY = cy - labelH / 2;

    if (labelW > 10) {
      const font = page.adjustFont(labelW, [nodes[nodeIdx].text], baseFont);
      page.drawString(
        nodes[nodeIdx].text,
        font,
        PdfBrush.White,
        null,
        new PdfRect(labelX, labelY, labelW, labelH),
        ETextAlign.CenterLeft
      );
    }

    // Also label in the RIGHT part
    const rLabelX = cx + innerRx + 4;
    const rLabelW = bandRx - 8;
    if (rLabelW > 10) {
      const font = page.adjustFont(rLabelW, [nodes[nodeIdx].text], baseFont);
      page.drawString(
        nodes[nodeIdx].text,
        font,
        PdfBrush.White,
        null,
        new PdfRect(rLabelX, cy - labelH / 2, rLabelW, labelH),
        ETextAlign.CenterLeft
      );
    }

    // Separator ring outline (white, subtle)
    page.drawEllipse(labelPen, null, new PdfRect(cx - rx, cy - ry, rx * 2, ry * 2));
  }
}

// ─── Layout: Spoke Wheel ──────────────────────────────────────────────────────

function drawSpokeWheel(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  // First node = hub, rest = spokes
  const hub = nodes[0];
  const spokeNodes = nodes.slice(1);
  const n = spokeNodes.length || 1;

  const nodeW = Math.min(rect.width * 0.22, rect.height * 0.22, 65);
  const nodeH = Math.min(nodeW * 0.5, 24);
  const hubR = Math.min(rect.width * 0.12, rect.height * 0.12, 32);

  // Orbit radius: leave room for node boxes
  const orbitR = Math.min(rect.width, rect.height) / 2 - nodeH - 4;

  // Draw hub circle
  const hubColorHex = colors[0];
  page.drawEllipse(
    null,
    mkBrush(hubColorHex),
    new PdfRect(cx - hubR, cy - hubR, hubR * 2, hubR * 2)
  );
  const hubFont = page.adjustFont(hubR * 1.6, [hub.text], baseFont);
  page.drawString(
    hub.text,
    hubFont,
    paletteTextBrush(palette),
    null,
    new PdfRect(cx - hubR, cy - hubR, hubR * 2, hubR * 2),
    ETextAlign.Center
  );

  if (spokeNodes.length === 0) return;

  const startAngle = -Math.PI / 2;
  const spokePositions = spokeNodes.map((_, i) => ({
    x: cx + Math.cos(startAngle + (2 * Math.PI * i) / n) * orbitR,
    y: cy + Math.sin(startAngle + (2 * Math.PI * i) / n) * orbitR,
  }));

  for (let i = 0; i < spokeNodes.length; i++) {
    const { x: nx, y: ny } = spokePositions[i];
    const colorHex = colors[(i + 1) % colors.length];
    const dx = nx - cx;
    const dy = ny - cy;
    const len = Math.sqrt(dx * dx + dy * dy);

    // Spoke line: from hub edge to node edge
    const fromX = cx + (dx / len) * (hubR + 2);
    const fromY = cy + (dy / len) * (hubR + 2);
    const toX = nx - (dx / len) * (nodeW * 0.5);
    const toY = ny - (dy / len) * (nodeH * 0.5);

    page.drawLine(mkPen(colorHex, 1.5), new PdfPoint(fromX, fromY), new PdfPoint(toX, toY));

    // Sub-labels on the spoke line (children shown as small dots with text)
    if (spokeNodes[i].children.length > 0) {
      const {children} = spokeNodes[i];
      for (let j = 0; j < children.length; j++) {
        const t = (j + 1) / (children.length + 1);
        const spx = fromX + (toX - fromX) * t;
        const spy = fromY + (toY - fromY) * t;
        const dotR = 3;
        page.drawEllipse(
          null,
          new PdfBrush(lightenColor(colorHex, 0.5)),
          new PdfRect(spx - dotR, spy - dotR, dotR * 2, dotR * 2)
        );
        // Small text perpendicular to spoke
        const perpX = -(dy / len) * 12;
        const perpY = (dx / len) * 12;
        const cf = new PdfFont(PdfFont.FONT_NAME, 6);
        page.drawString(
          children[j].text,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(spx + perpX - 20, spy + perpY - 5, 40, 10),
          ETextAlign.Center
        );
      }
    }

    // Outer node box
    const nodeRect = new PdfRect(nx - nodeW / 2, ny - nodeH / 2, nodeW, nodeH);
    page.drawRoundedRectangle(null, mkBrush(colorHex), nodeRect, 4);
    const font = page.adjustFont(nodeW - 8, [spokeNodes[i].text], baseFont);
    page.drawString(
      spokeNodes[i].text,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(nx - nodeW / 2 + 4, ny - nodeH / 2, nodeW - 8, nodeH),
      ETextAlign.Center
    );
  }
}

// ─── Layout: Timeline Vertical ────────────────────────────────────────────────

function drawTimelineVertical(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette
): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const cx = rect.left + rect.width / 2;
  const rowH = rect.height / n;
  const cardW = rect.width * 0.42;
  const cardH = Math.max(rowH - 8, 14);
  const dotR = 5;
  const linePen = mkPen(colors[0], 2);

  // Central vertical line
  page.drawLine(linePen, new PdfPoint(cx, rect.top), new PdfPoint(cx, rect.top + rect.height));

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const midY = rect.top + i * rowH + rowH / 2;
    const cardY = midY - cardH / 2;

    // Alternating: even = left of line, odd = right
    const isLeft = i % 2 === 0;
    const cardX = isLeft ? cx - cardW - dotR - 6 : cx + dotR + 6;

    // Connector line from card to dot
    const lineX1 = isLeft ? cardX + cardW : cardX;
    const lineX2 = isLeft ? cx - dotR : cx + dotR;
    page.drawLine(mkPen(colorHex, 1), new PdfPoint(lineX1, midY), new PdfPoint(lineX2, midY));

    // Dot on the spine
    page.drawEllipse(
      null,
      mkBrush(colorHex),
      new PdfRect(cx - dotR, midY - dotR, dotR * 2, dotR * 2)
    );
    // White inner dot
    const innerR = dotR * 0.45;
    page.drawEllipse(
      null,
      new PdfBrush(PdfColor.fromRgb(255, 255, 255)),
      new PdfRect(cx - innerR, midY - innerR, innerR * 2, innerR * 2)
    );

    // Card
    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(cardX, cardY, cardW, cardH), 3);

    // Title
    const titleH = node.children.length > 0 ? Math.min(cardH * 0.42, 18) : cardH;
    const font = page.adjustFont(cardW - 10, [node.text], baseFont);
    page.drawString(
      node.text,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(cardX + 5, cardY, cardW - 10, titleH),
      ETextAlign.CenterLeft
    );

    // Children
    if (node.children.length > 0) {
      const bodyH = cardH - titleH - 2;
      const childRowH = bodyH / node.children.length;
      const lightBg = new PdfBrush(lightenColor(colorHex, 0.78));
      page.drawRoundedRectangle(
        null,
        lightBg,
        new PdfRect(cardX, cardY + titleH + 1, cardW, bodyH),
        3
      );
      for (let j = 0; j < node.children.length; j++) {
        const cy2 = cardY + titleH + 1 + j * childRowH;
        const cf = page.adjustFont(cardW - 14, [node.children[j].text], childFont);
        page.drawString(
          `· ${node.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(cardX + 7, cy2, cardW - 14, childRowH),
          ETextAlign.CenterLeft
        );
      }
    }
  }
}

// ─── Layout: Fishbone (Ishikawa) ──────────────────────────────────────────────

function drawFishbone(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length < 2) {
    // Fallback: not enough for a fishbone
    drawList(page, rect, nodes, palette);
    return;
  }

  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);

  // Last node = the "effect" (right side)
  const effect = nodes[nodes.length - 1];
  const causes = nodes.slice(0, -1);
  const effectColorHex = colors[0];

  const effectW = Math.min(rect.width * 0.22, 80);
  const effectH = Math.min(rect.height * 0.25, 36);
  const cy = rect.top + rect.height / 2;

  // Spine: left to right
  const spineX1 = rect.left + 8;
  const spineX2 = rect.left + rect.width - effectW - 10;
  const spineLen = spineX2 - spineX1;

  const spinePen = mkPen(effectColorHex, 2);
  page.drawLine(spinePen, new PdfPoint(spineX1, cy), new PdfPoint(spineX2, cy));
  // Arrowhead on spine
  drawArrowHead(page, spineX1, cy, spineX2 + 4, cy, effectColorHex, 7);

  // Effect box
  const effectX = rect.left + rect.width - effectW - 2;
  const effectY = cy - effectH / 2;
  page.drawRoundedRectangle(
    null,
    mkBrush(effectColorHex),
    new PdfRect(effectX, effectY, effectW, effectH),
    4
  );
  const ef = page.adjustFont(effectW - 8, [effect.text], baseFont);
  page.drawString(
    effect.text,
    ef,
    paletteTextBrush(palette),
    null,
    new PdfRect(effectX + 4, effectY, effectW - 8, effectH),
    ETextAlign.Center
  );

  if (causes.length === 0) return;

  // Split causes into upper and lower
  const upper: SmartNode[] = [];
  const lower: SmartNode[] = [];
  causes.forEach((c, i) => (i % 2 === 0 ? upper : lower).push(c));

  const boneH = rect.height * 0.38; // length of bone in Y direction
  const boneLen = Math.sqrt(boneH * boneH + boneH * boneH); // 45° bone

  const drawBone = (cause: SmartNode, rootX: number, isUpper: boolean, causeIdx: number) => {
    const colorHex = colors[(causeIdx + 1) % colors.length];
    const bonePen = mkPen(colorHex, 1.5);
    const dir = isUpper ? -1 : 1; // -1 = upward, +1 = downward

    // Bone goes from root (on spine) diagonally to tip (up-left or down-left)
    const tipX = rootX - boneH;
    const tipY = cy + dir * boneH;

    page.drawLine(bonePen, new PdfPoint(rootX, cy), new PdfPoint(tipX, tipY));

    // Cause label at tip
    const labelW = Math.min(spineLen / (causes.length + 1), 60);
    const labelH = 12;
    const labelY = isUpper ? tipY - labelH - 2 : tipY + 2;
    const font = page.adjustFont(labelW + 20, [cause.text], new PdfFont(PdfFont.FONT_NAME, 8));
    page.drawString(
      cause.text,
      font,
      PdfBrush.Black,
      null,
      new PdfRect(tipX - 10, labelY, labelW + 20, labelH),
      ETextAlign.Center
    );

    // Sub-causes as small branches off the bone
    if (cause.children.length > 0) {
      const subPen = mkPen(colorHex, 0.75);
      for (let k = 0; k < cause.children.length; k++) {
        const t = (k + 1) / (cause.children.length + 1);
        const bx = rootX + (tipX - rootX) * t;
        const by = cy + (tipY - cy) * t;
        const subLen = boneH * 0.28;
        // Sub-bone perpendicular-ish: horizontal offset
        const sbEndX = bx - subLen * 0.7;
        const sbEndY = by + dir * subLen * 0.7;
        page.drawLine(subPen, new PdfPoint(bx, by), new PdfPoint(sbEndX, sbEndY));
        const sf = new PdfFont(PdfFont.FONT_NAME, 7);
        const subLabel = cause.children[k].text;
        const sly = isUpper ? sbEndY - 10 : sbEndY;
        page.drawString(
          subLabel,
          sf,
          PdfBrush.Black,
          null,
          new PdfRect(sbEndX - 25, sly, 50, 10),
          ETextAlign.Center
        );
      }
    }
  };

  // Place upper bones evenly along spine
  for (let i = 0; i < upper.length; i++) {
    const rootX = spineX1 + ((i + 1) * spineLen) / (upper.length + 1);
    drawBone(upper[i], rootX, true, i * 2);
  }
  // Place lower bones evenly along spine
  for (let i = 0; i < lower.length; i++) {
    const rootX = spineX1 + ((i + 1) * spineLen) / (lower.length + 1);
    drawBone(lower[i], rootX, false, i * 2 + 1);
  }
}

// ─── Layout: Flowchart ────────────────────────────────────────────────────────

function drawFlowchart(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const n = nodes.length;
  const gap = 6;
  const boxH = Math.min((rect.height - gap * (n - 1)) / n, 32);
  const boxW = rect.width * 0.65;
  const cx = rect.left + rect.width / 2;
  const boxLeft = cx - boxW / 2;

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const isDiamond = node.text.startsWith('?');
    const label = isDiamond ? node.text.slice(1).trim() : node.text;
    const colorHex = colors[i % colors.length];
    const y = rect.top + i * (boxH + gap);
    const midY = y + boxH / 2;

    if (isDiamond) {
      // Diamond shape
      const dw = boxW * 0.85;
      const dh = boxH * 1.1;
      const dx = cx - dw / 2;
      const dy = y + (boxH - dh) / 2;
      page.drawPolygon(null, mkBrush(colorHex), [
        new PdfPoint(cx, dy),
        new PdfPoint(dx + dw, dy + dh / 2),
        new PdfPoint(cx, dy + dh),
        new PdfPoint(dx, dy + dh / 2),
      ]);
      const font = page.adjustFont(dw * 0.65, [label], baseFont);
      page.drawString(
        label,
        font,
        paletteTextBrush(palette),
        null,
        new PdfRect(dx + dw * 0.18, dy + dh * 0.22, dw * 0.64, dh * 0.56),
        ETextAlign.Center
      );

      // "Yes/No" branch labels if children present
      if (node.children.length > 0) {
        const sf = new PdfFont(PdfFont.FONT_NAME, 7);
        page.drawString(
          node.children[0].text,
          sf,
          mkBrush(colorHex),
          null,
          new PdfRect(cx + dw / 2 + 2, dy + dh / 2 - 6, 24, 12),
          ETextAlign.CenterLeft
        );
        if (node.children.length > 1) {
          page.drawString(
            node.children[1].text,
            sf,
            mkBrush(colorHex),
            null,
            new PdfRect(cx - dw / 2 - 26, dy + dh / 2 - 6, 24, 12),
            ETextAlign.CenterLeft
          );
        }
      }
    } else {
      // Rounded rectangle
      page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(boxLeft, y, boxW, boxH), 4);
      const font = page.adjustFont(boxW - 12, [label], baseFont);
      page.drawString(
        label,
        font,
        paletteTextBrush(palette),
        null,
        new PdfRect(boxLeft + 6, y, boxW - 12, boxH),
        ETextAlign.Center
      );
    }

    // Arrow to next node
    if (i < n - 1) {
      const fromY = isDiamond ? y + boxH * 1.05 : y + boxH;
      const toY = rect.top + (i + 1) * (boxH + gap) - 2;
      const nextColor = colors[(i + 1) % colors.length];
      page.drawLine(mkPen(nextColor, 1.2), new PdfPoint(cx, fromY), new PdfPoint(cx, toY - 5));
      drawArrowHead(page, cx, fromY, cx, toY, nextColor, 6);
    }
  }
}

// ─── Layout: Venn ─────────────────────────────────────────────────────────────
// Input: 3 depth-0 items → left circle | overlap/shared | right circle

function drawVenn(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const overFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const leftNode = nodes[0];
  const overNode = nodes.length >= 3 ? nodes[1] : null;
  const rightNode = nodes.length >= 2 ? nodes[nodes.length - 1] : null;

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  // ── Geometry ──────────────────────────────────────────────────────────────
  // overlap_frac: share of 2r that forms the lens zone (35%)
  // Constraint: both circles must fit in rect.width
  //   lCx - r  ≥  rect.left   →  r  ≤  rect.width / (2 * (2 - f))
  const f = 0.35;
  const r = Math.min(rect.width / (2 * (2 - f)), rect.height * 0.45);
  const overlapW = 2 * r * f; // horizontal width of lens zone
  const d = 2 * r - overlapW; // center-to-center distance
  const lCx = cx - d / 2;
  const rCx = cx + d / 2;

  // Text zone x coords (exclusive left, lens, exclusive right)
  // Left   exclusive: lCx-r  ..  rCx-r  (width = d)
  // Lens:             rCx-r  ..  lCx+r  (width = overlapW, centred at cx)
  // Right  exclusive: lCx+r  ..  rCx+r  (width = d)
  const pad = 5;
  const leftX = lCx - r + pad;
  const leftW = d - pad * 2;
  const overX = cx - overlapW / 2 + pad;
  const overW = overlapW - pad * 2;
  const rightX = lCx + r + pad;
  const rightW = d - pad * 2;

  const titleY = cy - r + 7;
  const titleH = 13;
  const childY0 = cy - r * 0.52;
  const childAreaH = r * 1.3;

  const leftColor = colors[0];
  const rightColor = colors[Math.min(1, colors.length - 1)];

  // ── Draw circles (right drawn last so it covers the overlap fill) ─────────
  page.drawEllipse(
    null,
    new PdfBrush(lightenColor(leftColor, 0.72)),
    new PdfRect(lCx - r, cy - r, r * 2, r * 2)
  );
  page.drawEllipse(
    null,
    new PdfBrush(lightenColor(rightColor, 0.72)),
    new PdfRect(rCx - r, cy - r, r * 2, r * 2)
  );

  // Outlines on top
  page.drawEllipse(mkPen(leftColor, 1.5), null, new PdfRect(lCx - r, cy - r, r * 2, r * 2));
  page.drawEllipse(mkPen(rightColor, 1.5), null, new PdfRect(rCx - r, cy - r, r * 2, r * 2));

  // ── Labels & children ─────────────────────────────────────────────────────
  // Left
  if (leftW > 8) {
    page.drawString(
      leftNode.text,
      page.adjustFont(leftW, [leftNode.text], labelFont),
      mkBrush(leftColor),
      null,
      new PdfRect(leftX, titleY, leftW, titleH),
      ETextAlign.CenterLeft
    );
    if (leftNode.children.length > 0) {
      const rowH = Math.min(childAreaH / leftNode.children.length, 13);
      for (let j = 0; j < leftNode.children.length; j++) {
        const cf = page.adjustFont(leftW, [leftNode.children[j].text], childFont);
        page.drawString(
          `• ${leftNode.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(leftX, childY0 + j * rowH, leftW, rowH),
          ETextAlign.CenterLeft
        );
      }
    }
  }

  // Right
  if (rightNode && rightW > 8) {
    page.drawString(
      rightNode.text,
      page.adjustFont(rightW, [rightNode.text], labelFont),
      mkBrush(rightColor),
      null,
      new PdfRect(rightX, titleY, rightW, titleH),
      ETextAlign.CenterLeft
    );
    if (rightNode.children.length > 0) {
      const rowH = Math.min(childAreaH / rightNode.children.length, 13);
      for (let j = 0; j < rightNode.children.length; j++) {
        const cf = page.adjustFont(rightW, [rightNode.children[j].text], childFont);
        page.drawString(
          `• ${rightNode.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(rightX, childY0 + j * rowH, rightW, rowH),
          ETextAlign.CenterLeft
        );
      }
    }
  }

  // Overlap / lens
  if (overNode && overW > 8) {
    page.drawString(
      overNode.text,
      page.adjustFont(overW, [overNode.text], overFont),
      PdfBrush.Black,
      null,
      new PdfRect(overX, titleY, overW, titleH),
      ETextAlign.Center
    );
    if (overNode.children.length > 0) {
      const rowH = Math.min(childAreaH / overNode.children.length, 12);
      for (let j = 0; j < overNode.children.length; j++) {
        const cf = page.adjustFont(overW, [overNode.children[j].text], childFont);
        page.drawString(
          overNode.children[j].text,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(overX, childY0 + j * rowH, overW, rowH),
          ETextAlign.Center
        );
      }
    }
  }
}

// ─── Layout: Comparison ───────────────────────────────────────────────────────

function drawComparison(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 8);

  const n = Math.min(nodes.length, 4);
  const gap = 6;
  const colW = (rect.width - gap * (n - 1)) / n;
  const titleH = Math.min(rect.height * 0.2, 22);
  const bodyH = rect.height - titleH - 4;

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const x = rect.left + i * (colW + gap);

    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(x, rect.top, colW, titleH), 4);
    const tf = page.adjustFont(colW - 10, [node.text], baseFont);
    page.drawString(
      node.text,
      tf,
      paletteTextBrush(palette),
      null,
      new PdfRect(x + 5, rect.top, colW - 10, titleH),
      ETextAlign.Center
    );

    page.drawRoundedRectangle(
      null,
      new PdfBrush(lightenColor(colorHex, 0.85)),
      new PdfRect(x, rect.top + titleH + 2, colW, bodyH),
      4
    );

    const cn = node.children.length;
    if (cn > 0) {
      const rowH = bodyH / cn;
      for (let j = 0; j < cn; j++) {
        const ry = rect.top + titleH + 2 + j * rowH;
        const cf = page.adjustFont(colW - 14, [node.children[j].text], childFont);
        page.drawString(
          `• ${node.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(x + 7, ry + 2, colW - 14, rowH - 2),
          ETextAlign.CenterLeft
        );
      }
    }
  }
}

// ─── Layout: Double Bubble ────────────────────────────────────────────────────

function drawDoubleBubble(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette
): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const smallFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const leftNode = nodes[0];
  const rightNode = nodes.length >= 2 ? nodes[1] : null;

  const mainR = Math.min(rect.width * 0.14, rect.height * 0.25, 30);
  const satR = Math.min(mainR * 0.55, 18);
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const leftCx = cx - rect.width * 0.22;
  const rightCx = cx + rect.width * 0.22;

  const leftColor = colors[0];
  const rightColor = colors[Math.min(1, colors.length - 1)];

  const drawSatellites = (
    mainCx: number,
    children: SmartNode[],
    colorHex: string,
    baseAngle: number
  ) => {
    const sn = children.length;
    if (sn === 0) return;
    const spread = (Math.PI * 2) / 3; // 120° — keeps all satellites within rect
    const dist = mainR + satR + 8;
    for (let i = 0; i < sn; i++) {
      const angle = sn === 1 ? baseAngle : baseAngle - spread / 2 + (i * spread) / (sn - 1);
      const sx = mainCx + Math.cos(angle) * dist;
      const sy = cy + Math.sin(angle) * dist;
      page.drawLine(mkPen(colorHex, 0.75), new PdfPoint(mainCx, cy), new PdfPoint(sx, sy));
      page.drawEllipse(
        null,
        new PdfBrush(lightenColor(colorHex, 0.55)),
        new PdfRect(sx - satR, sy - satR, satR * 2, satR * 2)
      );
      page.drawEllipse(
        mkPen(colorHex, 1),
        null,
        new PdfRect(sx - satR, sy - satR, satR * 2, satR * 2)
      );
      const sf = page.adjustFont(satR * 1.6, [children[i].text], smallFont);
      page.drawString(
        children[i].text,
        sf,
        PdfBrush.Black,
        null,
        new PdfRect(sx - satR + 2, sy - satR + 2, satR * 2 - 4, satR * 2 - 4),
        ETextAlign.Center
      );
    }
  };

  // Satellites first (drawn behind main circles)
  drawSatellites(leftCx, leftNode.children, leftColor, Math.PI);
  if (rightNode) drawSatellites(rightCx, rightNode.children, rightColor, 0);

  // Connector line between main circles
  if (rightNode) {
    page.drawLine(
      mkPen(colors[Math.min(2, colors.length - 1)], 1.5),
      new PdfPoint(leftCx + mainR, cy),
      new PdfPoint(rightCx - mainR, cy)
    );
  }

  // Main circles on top
  page.drawEllipse(
    null,
    mkBrush(leftColor),
    new PdfRect(leftCx - mainR, cy - mainR, mainR * 2, mainR * 2)
  );
  const lf = page.adjustFont(mainR * 1.6, [leftNode.text], baseFont);
  page.drawString(
    leftNode.text,
    lf,
    paletteTextBrush(palette),
    null,
    new PdfRect(leftCx - mainR + 4, cy - mainR + 4, mainR * 2 - 8, mainR * 2 - 8),
    ETextAlign.Center
  );

  if (rightNode) {
    page.drawEllipse(
      null,
      mkBrush(rightColor),
      new PdfRect(rightCx - mainR, cy - mainR, mainR * 2, mainR * 2)
    );
    const rf = page.adjustFont(mainR * 1.6, [rightNode.text], baseFont);
    page.drawString(
      rightNode.text,
      rf,
      paletteTextBrush(palette),
      null,
      new PdfRect(rightCx - mainR + 4, cy - mainR + 4, mainR * 2 - 8, mainR * 2 - 8),
      ETextAlign.Center
    );
  }
}

// ─── Layout: Chevron ──────────────────────────────────────────────────────────

function drawChevron(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const n = nodes.length;
  const tipW = Math.min(rect.height * 0.35, rect.width * 0.1);
  const step = (rect.width - tipW) / n;
  const chevW = step + tipW;
  const cy = rect.top + rect.height / 2;
  const yTop = rect.top;
  const yBot = rect.top + rect.height;

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const x = rect.left + i * step;

    const pts: PdfPoint[] =
      i === 0
        ? [
            new PdfPoint(x, yTop),
            new PdfPoint(x + step, yTop),
            new PdfPoint(x + chevW, cy),
            new PdfPoint(x + step, yBot),
            new PdfPoint(x, yBot),
          ]
        : [
            new PdfPoint(x, yTop),
            new PdfPoint(x + step, yTop),
            new PdfPoint(x + chevW, cy),
            new PdfPoint(x + step, yBot),
            new PdfPoint(x, yBot),
            new PdfPoint(x + tipW, cy),
          ];

    page.drawPolygon(null, mkBrush(colorHex), pts);

    const textX = i === 0 ? x + 5 : x + tipW + 4;
    const textW = step - (i === 0 ? 10 : tipW + 4) - 4;
    if (textW > 6) {
      const font = page.adjustFont(textW, [node.text], baseFont);
      page.drawString(
        node.text,
        font,
        paletteTextBrush(palette),
        null,
        new PdfRect(textX, yTop + rect.height * 0.15, textW, rect.height * 0.38),
        ETextAlign.Center
      );
      if (node.children.length > 0) {
        const childAreaH = rect.height * 0.35;
        const rowH = childAreaH / node.children.length;
        for (let j = 0; j < node.children.length; j++) {
          const cf = page.adjustFont(textW, [node.children[j].text], childFont);
          page.drawString(
            `· ${node.children[j].text}`,
            cf,
            paletteTextBrush(palette),
            null,
            new PdfRect(textX, cy + 4 + j * rowH, textW, rowH),
            ETextAlign.Center
          );
        }
      }
    }
  }
}

// ─── Layout: Inverted Pyramid ─────────────────────────────────────────────────

function drawPyramidInverted(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette
): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const n = nodes.length;
  const gap = 2;
  const bandH = (rect.height - gap * (n - 1)) / n;
  const cx = rect.left + rect.width / 2;

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    // Top band widest (i=0 → 100%), bottom narrowest
    const widthFrac = 1 - (i / n) * 0.65;
    const bandW = rect.width * widthFrac;
    const bx = cx - bandW / 2;
    const by = rect.top + i * (bandH + gap);

    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(bx, by, bandW, bandH), 3);
    const font = page.adjustFont(bandW - 10, [node.text], baseFont);
    page.drawString(
      node.text,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(bx + 5, by, bandW - 10, bandH),
      ETextAlign.Center
    );

    if (node.children.length > 0) {
      const sideW = (rect.width - bandW) / 2 - 4;
      if (sideW > 20) {
        const tagH = Math.min(bandH * 0.55, 12);
        const tagY = by + (bandH - tagH) / 2;
        const rx = cx + bandW / 2 + 3;
        for (let j = 0; j < Math.min(node.children.length, 2); j++) {
          const cf = page.adjustFont(sideW, [node.children[j].text], childFont);
          page.drawString(
            `· ${node.children[j].text}`,
            cf,
            mkBrush(colorHex),
            null,
            new PdfRect(rx, tagY + j * (tagH + 1), sideW, tagH),
            ETextAlign.CenterLeft
          );
        }
      }
    }
  }
}

// ─── Layout: Hierarchy Left ───────────────────────────────────────────────────

function drawHierarchyLeft(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette
): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);

  const countLeaves = (n: SmartNode): number =>
    n.children.length === 0 ? 1 : n.children.reduce((s, c) => s + countLeaves(c), 0);

  const totalLeaves = nodes.reduce((s, n) => s + countLeaves(n), 0);
  const maxDepth = (function md(ns: SmartNode[], d: number): number {
    return ns.reduce((m, n) => Math.max(m, md(n.children, d + 1)), d);
  })(nodes, 0);

  interface HLPos {
    node: SmartNode;
    col: number;
    rowFrac: number;
  }
  const allPos: HLPos[] = [];

  const layout = (ns: SmartNode[], col: number, yStart: number) => {
    let y = yStart;
    for (const n of ns) {
      const h = countLeaves(n) / totalLeaves;
      allPos.push({ node: n, col, rowFrac: y + h / 2 });
      if (n.children.length > 0) layout(n.children, col + 1, y);
      y += h;
    }
  };
  layout(nodes, 0, 0);

  const numCols = maxDepth + 1;
  const colW = rect.width / numCols;
  const boxW = Math.min(colW * 0.82, 65);
  const boxH = Math.min(rect.height / (totalLeaves + 1), 26);
  const pen = mkPen(colors[0], 0.75);

  const getXY = (p: HLPos) => ({
    px: rect.left + p.col * colW + colW / 2,
    py: rect.top + p.rowFrac * rect.height,
  });

  // Connections first
  for (const np of allPos) {
    const { px: fromX, py: fromY } = getXY(np);
    for (const child of np.node.children) {
      const childPos = allPos.find((p) => p.node === child);
      if (!childPos) continue;
      const { px: toX, py: toY } = getXY(childPos);
      const midX = fromX + colW * 0.5;
      page.drawLine(pen, new PdfPoint(fromX + boxW / 2, fromY), new PdfPoint(midX, fromY));
      page.drawLine(pen, new PdfPoint(midX, fromY), new PdfPoint(midX, toY));
      page.drawLine(pen, new PdfPoint(midX, toY), new PdfPoint(toX - boxW / 2, toY));
    }
  }

  // Boxes on top
  for (const np of allPos) {
    const { px, py } = getXY(np);
    const colorHex = colors[np.col % colors.length];
    const bx = px - boxW / 2;
    const by = py - boxH / 2;
    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(bx, by, boxW, boxH), 3);
    const font = page.adjustFont(boxW - 8, [np.node.text], baseFont);
    page.drawString(
      np.node.text,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(bx + 4, by, boxW - 8, boxH),
      ETextAlign.Center
    );
  }
}

// ─── Layout: Bow-Tie ──────────────────────────────────────────────────────────

function drawBowTie(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const leftNode = nodes[0];
  const centerNode = nodes.length >= 3 ? nodes[1] : null;
  const rightNode = nodes.length >= 2 ? nodes[nodes.length - 1] : null;

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const centerR = Math.min(rect.height * 0.18, rect.width * 0.12, 22);
  const gap = centerR + 5;
  const yTop = rect.top;
  const yBot = rect.top + rect.height;
  const xLeft = rect.left;
  const xRight = rect.left + rect.width;

  const leftColor = colors[0];
  const centerColor = colors[Math.min(1, colors.length - 1)];
  const rightColor = colors[Math.min(2, colors.length - 1)];

  // Left triangle (wide-left, point-right)
  page.drawPolygon(null, mkBrush(leftColor), [
    new PdfPoint(xLeft, yTop),
    new PdfPoint(cx - gap, cy),
    new PdfPoint(xLeft, yBot),
  ]);

  // Right triangle (wide-right, point-left)
  if (rightNode) {
    page.drawPolygon(null, mkBrush(rightColor), [
      new PdfPoint(xRight, yTop),
      new PdfPoint(cx + gap, cy),
      new PdfPoint(xRight, yBot),
    ]);
  }

  // Center circle
  page.drawEllipse(
    null,
    mkBrush(centerNode ? centerColor : leftColor),
    new PdfRect(cx - centerR, cy - centerR, centerR * 2, centerR * 2)
  );

  // ── Text content ────────────────────────────────────────────────────────────
  // The triangle body spans from the wide end to the tip.
  // We divide it into a label zone (near wide end) and a children zone.
  const leftBodyW = cx - gap - xLeft; // distance from xLeft to triangle tip
  const rightBodyW = xRight - cx - gap;

  // Label zone: positioned at 1/3 of the way from the wide end toward the tip
  const labelZoneW = Math.min(leftBodyW * 0.55, 65);
  const labelZoneH = Math.min(rect.height * 0.22, 20);

  // Left main label — anchored near the wide (left) end
  const lf = page.adjustFont(labelZoneW, [leftNode.text], baseFont);
  page.drawString(
    leftNode.text,
    lf,
    paletteTextBrush(palette),
    null,
    new PdfRect(xLeft + 6, cy - labelZoneH / 2, labelZoneW, labelZoneH),
    ETextAlign.CenterLeft
  );

  // Left children — distributed vertically across the triangle body
  if (leftNode.children.length > 0) {
    const halfH = rect.height * 0.62;
    const rowH = halfH / leftNode.children.length;
    const cFont = new PdfFont(PdfFont.FONT_NAME, 7);
    for (let j = 0; j < leftNode.children.length; j++) {
      const ty = cy - halfH / 2 + (j + 0.5) * rowH - rowH / 2;
      // Width available narrows toward the tip
      const dist2tip = Math.abs(ty + rowH / 2 - cy) / (rect.height / 2);
      const availW = Math.max(leftBodyW * (0.3 + dist2tip * 0.35), 20);
      const cf = page.adjustFont(availW, [leftNode.children[j].text], cFont);
      page.drawString(
        `· ${leftNode.children[j].text}`,
        cf,
        paletteTextBrush(palette),
        null,
        new PdfRect(xLeft + 8, ty, availW, rowH),
        ETextAlign.CenterLeft
      );
    }
  }

  // Center label
  if (centerNode) {
    const cf = page.adjustFont(centerR * 1.6, [centerNode.text], new PdfFont(PdfFont.FONT_NAME, 7));
    page.drawString(
      centerNode.text,
      cf,
      paletteTextBrush(palette),
      null,
      new PdfRect(cx - centerR + 2, cy - centerR + 2, centerR * 2 - 4, centerR * 2 - 4),
      ETextAlign.Center
    );
  }

  // Right main label — anchored near the wide (right) end
  if (rightNode) {
    const rLabelX = xRight - labelZoneW - 6;
    const rf = page.adjustFont(labelZoneW, [rightNode.text], baseFont);
    page.drawString(
      rightNode.text,
      rf,
      paletteTextBrush(palette),
      null,
      new PdfRect(rLabelX, cy - labelZoneH / 2, labelZoneW, labelZoneH),
      ETextAlign.CenterLeft
    );

    if (rightNode.children.length > 0) {
      const halfH = rect.height * 0.62;
      const rowH = halfH / rightNode.children.length;
      const cFont = new PdfFont(PdfFont.FONT_NAME, 7);
      for (let j = 0; j < rightNode.children.length; j++) {
        const ty = cy - halfH / 2 + (j + 0.5) * rowH - rowH / 2;
        const dist2tip = Math.abs(ty + rowH / 2 - cy) / (rect.height / 2);
        const availW = Math.max(rightBodyW * (0.3 + dist2tip * 0.35), 20);
        const startX = xRight - availW - 8;
        const cf = page.adjustFont(availW, [rightNode.children[j].text], cFont);
        page.drawString(
          `· ${rightNode.children[j].text}`,
          cf,
          paletteTextBrush(palette),
          null,
          new PdfRect(startX, ty, availW, rowH),
          ETextAlign.CenterLeft
        );
      }
    }
  }
}

// ─── Layout: Gantt ────────────────────────────────────────────────────────────

function drawGantt(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const smallFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const n = nodes.length;
  const labelW = Math.min(rect.width * 0.28, 80);
  const chartX = rect.left + labelW + 5;
  const chartW = rect.width - labelW - 5;
  const gap = 3;
  const rowH = (rect.height - gap * (n - 1)) / n;
  const barH = Math.min(rowH * 0.72, 18);
  const barTop = (rowH - barH) / 2;

  // Vertical grid lines
  const gridPen = mkPen('#DDDDDD', 0.5);
  for (let g = 0; g <= 5; g++) {
    const gx = chartX + (g / 5) * chartW;
    page.drawLine(gridPen, new PdfPoint(gx, rect.top), new PdfPoint(gx, rect.top + rect.height));
  }

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const y = rect.top + i * (rowH + gap);

    const lf = page.adjustFont(labelW - 6, [node.text], baseFont);
    page.drawString(
      node.text,
      lf,
      PdfBrush.Black,
      null,
      new PdfRect(rect.left, y, labelW - 3, rowH),
      ETextAlign.CenterRight
    );

    if (node.children.length > 0) {
      const sn = node.children.length;
      const segW = chartW / sn;
      for (let j = 0; j < sn; j++) {
        const segX = chartX + j * segW;
        const segBg = new PdfBrush(lightenColor(colorHex, j * 0.12));
        page.drawRoundedRectangle(
          null,
          segBg,
          new PdfRect(segX + 1, y + barTop, segW - 2, barH),
          2
        );
        const sf = page.adjustFont(segW - 6, [node.children[j].text], smallFont);
        page.drawString(
          node.children[j].text,
          sf,
          PdfBrush.Black,
          null,
          new PdfRect(segX + 1, y + barTop, segW - 2, barH),
          ETextAlign.Center
        );
      }
    } else {
      const fill = chartW * Math.max(0.2, 1 - i * 0.1);
      page.drawRoundedRectangle(
        null,
        mkBrush(colorHex),
        new PdfRect(chartX, y + barTop, fill, barH),
        2
      );
    }
  }
}

// ─── Layout: Swim Lane ────────────────────────────────────────────────────────

function drawSwimLane(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const n = nodes.length;
  const headerW = Math.min(rect.width * 0.2, 60);
  const bodyW = rect.width - headerW - 2;
  const gap = 2;
  const laneH = (rect.height - gap * (n - 1)) / n;

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const y = rect.top + i * (laneH + gap);

    // Lane header
    page.drawRoundedRectangle(
      null,
      mkBrush(colorHex),
      new PdfRect(rect.left, y, headerW, laneH),
      3
    );
    const lf = page.adjustFont(laneH - 6, [node.text], baseFont);
    page.drawString(
      node.text,
      lf,
      paletteTextBrush(palette),
      null,
      new PdfRect(rect.left + 3, y, headerW - 6, laneH),
      ETextAlign.Center
    );

    // Lane body
    page.drawRoundedRectangle(
      null,
      new PdfBrush(lightenColor(colorHex, 0.88)),
      new PdfRect(rect.left + headerW + 2, y, bodyW, laneH),
      3
    );

    // Activity boxes
    if (node.children.length > 0) {
      const cn = node.children.length;
      const actGap = 4;
      const actW = (bodyW - actGap * (cn + 1)) / cn;
      const actH = laneH * 0.7;
      const actY = y + (laneH - actH) / 2;
      for (let j = 0; j < cn; j++) {
        const ax = rect.left + headerW + 2 + actGap + j * (actW + actGap);
        page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(ax, actY, actW, actH), 2);
        const cf = page.adjustFont(actW - 6, [node.children[j].text], childFont);
        page.drawString(
          node.children[j].text,
          cf,
          paletteTextBrush(palette),
          null,
          new PdfRect(ax + 3, actY, actW - 6, actH),
          ETextAlign.Center
        );
      }
    }
  }
}

// ─── Layout: Treemap ──────────────────────────────────────────────────────────

function drawTreemap(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const gap = 2;
  const n = nodes.length;

  const countLeaves = (nd: SmartNode): number =>
    nd.children.length === 0 ? 1 : nd.children.reduce((s, c) => s + countLeaves(c), 0);

  const totalLeaves = nodes.reduce((s, nd) => s + countLeaves(nd), 0);

  let x = rect.left;
  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const w = ((rect.width - gap * (n - 1)) * countLeaves(node)) / totalLeaves;

    if (node.children.length === 0) {
      page.drawRoundedRectangle(
        null,
        mkBrush(colorHex),
        new PdfRect(x, rect.top, w, rect.height),
        3
      );
      const font = page.adjustFont(w - 8, [node.text], baseFont);
      page.drawString(
        node.text,
        font,
        paletteTextBrush(palette),
        null,
        new PdfRect(x + 4, rect.top, w - 8, rect.height),
        ETextAlign.Center
      );
    } else {
      const titleH = Math.min(rect.height * 0.25, 20);
      page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(x, rect.top, w, titleH), 3);
      const tf = page.adjustFont(w - 8, [node.text], baseFont);
      page.drawString(
        node.text,
        tf,
        paletteTextBrush(palette),
        null,
        new PdfRect(x + 4, rect.top, w - 8, titleH),
        ETextAlign.Center
      );

      const bodyH = rect.height - titleH - gap;
      const bodyY = rect.top + titleH + gap;
      const cn = node.children.length;

      if (w >= rect.height * 0.5) {
        // Wide block → children stacked vertically
        const childH = (bodyH - gap * (cn - 1)) / cn;
        for (let j = 0; j < cn; j++) {
          const childY = bodyY + j * (childH + gap);
          page.drawRoundedRectangle(
            null,
            new PdfBrush(lightenColor(colorHex, 0.65 + j * 0.06)),
            new PdfRect(x, childY, w, childH),
            2
          );
          const cf = page.adjustFont(w - 8, [node.children[j].text], childFont);
          page.drawString(
            node.children[j].text,
            cf,
            PdfBrush.Black,
            null,
            new PdfRect(x + 4, childY, w - 8, childH),
            ETextAlign.Center
          );
        }
      } else {
        // Narrow block → children side by side horizontally
        const childW = (w - gap * (cn - 1)) / cn;
        for (let j = 0; j < cn; j++) {
          const childX = x + j * (childW + gap);
          page.drawRoundedRectangle(
            null,
            new PdfBrush(lightenColor(colorHex, 0.65 + j * 0.06)),
            new PdfRect(childX, bodyY, childW, bodyH),
            2
          );
          const cf = page.adjustFont(childW - 8, [node.children[j].text], childFont);
          page.drawString(
            node.children[j].text,
            cf,
            PdfBrush.Black,
            null,
            new PdfRect(childX + 4, bodyY, childW - 8, bodyH),
            ETextAlign.Center
          );
        }
      }
    }

    x += w + gap;
  }
}

// ─── V4 Layouts ───────────────────────────────────────────────────────────────

// decision-tree: nodes[0] = root question, nodes[0].children = branches, each branch's children = outcomes
function drawDecisionTree(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette
): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6);

  // Flatten all nodes into positional map: {node, x, y, w, h, colorHex}
  interface NodePos {
    node: SmartNode;
    cx: number;
    cy: number;
    colorHex: string;
  }
  const positions: NodePos[] = [];

  const root = nodes[0];
  const branches = root.children.length > 0 ? root.children : nodes.slice(1);
  const nb = branches.length;

  // Vertical layout: root at top, branches spread horizontally in middle row, leaves at bottom
  const rowH = rect.height / 3;
  const nodeW = Math.min(rect.width * 0.22, 55);
  const nodeH = Math.min(rowH * 0.5, 22);
  const rootCx = rect.left + rect.width / 2;
  const rootCy = rect.top + rowH * 0.35;

  // Draw root as diamond
  const diam = Math.min(nodeW, nodeH + 10);
  const rootColor = colors[0];
  const pts = [
    new PdfPoint(rootCx, rootCy - diam * 0.55),
    new PdfPoint(rootCx + diam, rootCy),
    new PdfPoint(rootCx, rootCy + diam * 0.55),
    new PdfPoint(rootCx - diam, rootCy),
  ];
  page.drawPolygon(mkPen(rootColor, 1), mkBrush(rootColor), pts);
  const rf = page.adjustFont(diam * 1.4 - 6, [root.text], baseFont);
  page.drawString(
    root.text,
    rf,
    paletteTextBrush(palette),
    null,
    new PdfRect(rootCx - diam * 0.7 + 2, rootCy - diam * 0.4, diam * 1.4 - 4, diam * 0.8),
    ETextAlign.Center
  );
  positions.push({ node: root, cx: rootCx, cy: rootCy, colorHex: rootColor });

  const branchY = rect.top + rowH * 1.25;
  const leafY = rect.top + rowH * 2.35;
  const branchXs: number[] = [];
  for (let i = 0; i < nb; i++) {
    branchXs.push(rect.left + (rect.width / nb) * (i + 0.5));
  }

  for (let i = 0; i < nb; i++) {
    const branch = branches[i];
    const colorHex = colors[(i + 1) % colors.length];
    const bx = branchXs[i];

    // Line from root to branch
    page.drawLine(
      mkPen(colorHex, 1.2),
      new PdfPoint(rootCx, rootCy + diam * 0.55),
      new PdfPoint(bx, branchY - nodeH / 2)
    );
    // Branch box (rounded)
    const bRect = new PdfRect(bx - nodeW / 2, branchY - nodeH / 2, nodeW, nodeH);
    page.drawRoundedRectangle(mkPen(colorHex, 1), mkBrush(colorHex), bRect, 4);
    const bf = page.adjustFont(nodeW - 6, [branch.text], baseFont);
    page.drawString(branch.text, bf, paletteTextBrush(palette), null, bRect, ETextAlign.Center);
    positions.push({ node: branch, cx: bx, cy: branchY, colorHex });

    // Leaves
    const leaves = branch.children;
    const nl = leaves.length;
    if (nl === 0) continue;
    const slotW = rect.width / nb;
    const leafW = Math.min((slotW * 0.8) / nl - 4, 40);
    const leafH = Math.min(rowH * 0.38, 16);
    for (let j = 0; j < nl; j++) {
      const lx = bx - slotW * 0.4 + ((slotW * 0.8) / nl) * (j + 0.5);
      const lColor = lightenColor(colorHex, 0.45);
      page.drawLine(
        mkPen(colorHex, 1),
        new PdfPoint(bx, branchY + nodeH / 2),
        new PdfPoint(lx, leafY - leafH / 2)
      );
      const lRect = new PdfRect(lx - leafW / 2, leafY - leafH / 2, leafW, leafH);
      page.drawRoundedRectangle(null, new PdfBrush(lColor), lRect, 3);
      const lf = page.adjustFont(leafW - 4, [leaves[j].text], labelFont);
      page.drawString(leaves[j].text, lf, PdfBrush.Black, null, lRect, ETextAlign.Center);
    }
  }
}

// funnel-wide: wide trapezoid funnel narrowing top→bottom; each node = funnel layer
function drawFunnelWide(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const gap = 2;
  const totalH = rect.height - gap * (n - 1);
  const rowH = totalH / n;

  for (let i = 0; i < n; i++) {
    const colorHex = colors[i % colors.length];
    const y = rect.top + i * (rowH + gap);
    // Width narrows linearly: top = full width, bottom row = 40% width
    const tFrac = 1 - (i / n) * 0.6;
    const bFrac = 1 - ((i + 1) / n) * 0.6;
    const tW = rect.width * tFrac;
    const bW = rect.width * bFrac;
    const cx = rect.left + rect.width / 2;
    const pts = [
      new PdfPoint(cx - tW / 2, y),
      new PdfPoint(cx + tW / 2, y),
      new PdfPoint(cx + bW / 2, y + rowH),
      new PdfPoint(cx - bW / 2, y + rowH),
    ];
    page.drawPolygon(mkPen(colorHex, 1), mkBrush(colorHex), pts);
    const labelW = Math.min(tW, bW) - 8;
    const font = page.adjustFont(labelW, [nodes[i].text], new PdfFont(PdfFont.FONT_NAME, 8));
    page.drawString(
      nodes[i].text,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(cx - labelW / 2, y, labelW, rowH),
      ETextAlign.Center
    );
  }
}

// iceberg: waterline divides content into "above" (visible) and "below" (hidden)
// nodes[0] = above-water items, nodes[1] = below-water items (or children of root)
function drawIceberg(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const aboveCol = colors[0];
  const belowCol = colors[1 % colors.length];
  const waterlineY = rect.top + rect.height * 0.35;
  const waterColor = '#2196F3';
  const cx = rect.left + rect.width / 2;

  // Above-water: peaked triangle
  const aboveW = rect.width * 0.55;
  const abovePts = [
    new PdfPoint(cx, rect.top + 4),
    new PdfPoint(cx + aboveW / 2, waterlineY),
    new PdfPoint(cx - aboveW / 2, waterlineY),
  ];
  page.drawPolygon(mkPen(aboveCol, 1), mkBrush(aboveCol), abovePts);

  // Waterline
  const waterPen = new PdfPen(PdfColor.fromRgb(33, 150, 243), 2);
  page.drawLine(
    waterPen,
    new PdfPoint(rect.left, waterlineY),
    new PdfPoint(rect.left + rect.width, waterlineY)
  );

  // Below-water: larger trapezoid (wider at waterline, narrower at bottom)
  const belowTopW = rect.width * 0.78;
  const belowBotW = rect.width * 0.38;
  const botY = rect.top + rect.height - 4;
  const belowPts = [
    new PdfPoint(cx - belowTopW / 2, waterlineY),
    new PdfPoint(cx + belowTopW / 2, waterlineY),
    new PdfPoint(cx + belowBotW / 2, botY),
    new PdfPoint(cx - belowBotW / 2, botY),
  ];
  page.drawPolygon(mkPen(belowCol, 1), mkBrush(belowCol), belowPts);

  // Labels
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const above = nodes[0];
  const belowNodes = nodes[0].children.length > 0 ? nodes[0].children : nodes.slice(1);

  // Above text
  const af = page.adjustFont(aboveW - 10, [above.text], baseFont);
  page.drawString(
    above.text,
    af,
    paletteTextBrush(palette),
    null,
    new PdfRect(cx - aboveW / 2 + 4, rect.top + 4, aboveW - 8, waterlineY - rect.top - 8),
    ETextAlign.Center
  );

  // Below items stacked
  const belowH = botY - waterlineY - 8;
  const nb = Math.max(belowNodes.length, 1);
  const rowH = belowH / nb;
  for (let i = 0; i < belowNodes.length; i++) {
    const ty = waterlineY + 4 + i * rowH;
    const frac = i / Math.max(belowNodes.length - 1, 1);
    const availW = belowTopW - (belowTopW - belowBotW) * frac - 10;
    const bf = page.adjustFont(availW, [belowNodes[i].text], baseFont);
    page.drawString(
      belowNodes[i].text,
      bf,
      paletteTextBrush(palette),
      null,
      new PdfRect(cx - availW / 2, ty, availW, rowH),
      ETextAlign.Center
    );
  }

  // "Visible" / "Hidden" labels on waterline
  const wf = new PdfFont(PdfFont.FONT_NAME, 6);
  page.drawString(
    'Visible',
    wf,
    new PdfBrush(PdfColor.fromRgb(33, 150, 243)),
    null,
    new PdfRect(rect.left, waterlineY - 12, 36, 10),
    ETextAlign.CenterLeft
  );
  page.drawString(
    'Hidden',
    wf,
    new PdfBrush(PdfColor.fromRgb(33, 150, 243)),
    null,
    new PdfRect(rect.left, waterlineY + 2, 36, 10),
    ETextAlign.CenterLeft
  );
}

// scales: balance scale with two sides; nodes[0]=left pan items, nodes[1]=right pan items
function drawScales(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  const {colors} = palette;
  const cx = rect.left + rect.width / 2;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const titleFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const blackPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1.5);

  // Two columns: items fill top-to-bottom, scale hardware at bottom
  const colW = rect.width * 0.41;
  const leftColX = rect.left + 4;
  const rightColX = rect.left + rect.width - colW - 4;
  const leftCx = leftColX + colW / 2;
  const rightCx = rightColX + colW / 2;

  const titleH = 14;
  const itemAreaT = rect.top + titleH + 3;
  // Pan-bar sits at 63% height — chains and scale hardware below
  const panBarY = rect.top + rect.height * 0.63;
  const itemAreaH = panBarY - itemAreaT - 2;

  const leftItems =
    nodes.length > 0 ? (nodes[0].children.length > 0 ? nodes[0].children : [nodes[0]]) : [];
  const rightItems =
    nodes.length > 1 ? (nodes[1].children.length > 0 ? nodes[1].children : [nodes[1]]) : [];
  const maxItems = Math.max(leftItems.length, rightItems.length, 1);
  const itemGap = 2;
  const itemH = Math.min(18, (itemAreaH - itemGap * (maxItems - 1)) / maxItems);

  // Pan title labels at the very top of each column
  if (nodes.length > 0) {
    const lf = page.adjustFont(colW - 4, [nodes[0].text], titleFont);
    page.drawString(
      nodes[0].text,
      lf,
      mkBrush(colors[0]),
      null,
      new PdfRect(leftColX, rect.top, colW, titleH),
      ETextAlign.Center
    );
  }
  if (nodes.length > 1) {
    const rf = page.adjustFont(colW - 4, [nodes[1].text], titleFont);
    page.drawString(
      nodes[1].text,
      rf,
      mkBrush(colors[1 % colors.length]),
      null,
      new PdfRect(rightColX, rect.top, colW, titleH),
      ETextAlign.Center
    );
  }

  // Left items (top-to-bottom)
  for (let i = 0; i < leftItems.length; i++) {
    const iy = itemAreaT + i * (itemH + itemGap);
    const ir = new PdfRect(leftColX, iy, colW, itemH);
    page.drawRoundedRectangle(null, mkBrush(colors[0]), ir, 3);
    const lf = page.adjustFont(colW - 6, [leftItems[i].text], baseFont);
    page.drawString(leftItems[i].text, lf, paletteTextBrush(palette), null, ir, ETextAlign.Center);
  }
  // Right items (top-to-bottom)
  for (let i = 0; i < rightItems.length; i++) {
    const iy = itemAreaT + i * (itemH + itemGap);
    const ir = new PdfRect(rightColX, iy, colW, itemH);
    page.drawRoundedRectangle(null, mkBrush(colors[1 % colors.length]), ir, 3);
    const rf = page.adjustFont(colW - 6, [rightItems[i].text], baseFont);
    page.drawString(rightItems[i].text, rf, paletteTextBrush(palette), null, ir, ETextAlign.Center);
  }

  // Pan bars (colored horizontal line at panBarY under each column)
  page.drawLine(
    mkPen(colors[0], 3),
    new PdfPoint(leftColX, panBarY),
    new PdfPoint(leftColX + colW, panBarY)
  );
  page.drawLine(
    mkPen(colors[1 % colors.length], 3),
    new PdfPoint(rightColX, panBarY),
    new PdfPoint(rightColX + colW, panBarY)
  );

  // Chains (V-shape: two lines from column thirds up to beam attachment points)
  const tiltDelta = rect.height * 0.018; // left side slightly lower
  const chainH = rect.height * 0.08;
  const beamY = panBarY + chainH;
  const beamHalfW = rect.width * 0.36;
  const leftBeamX = cx - beamHalfW;
  const rightBeamX = cx + beamHalfW;
  const leftBeamY = beamY + tiltDelta;
  const rightBeamY = beamY - tiltDelta;

  page.drawLine(
    new PdfPen(PdfColor.fromRgb(80, 80, 80), 0.8),
    new PdfPoint(leftColX + colW * 0.25, panBarY),
    new PdfPoint(leftBeamX, leftBeamY)
  );
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(80, 80, 80), 0.8),
    new PdfPoint(leftColX + colW * 0.75, panBarY),
    new PdfPoint(leftBeamX, leftBeamY)
  );
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(80, 80, 80), 0.8),
    new PdfPoint(rightColX + colW * 0.25, panBarY),
    new PdfPoint(rightBeamX, rightBeamY)
  );
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(80, 80, 80), 0.8),
    new PdfPoint(rightColX + colW * 0.75, panBarY),
    new PdfPoint(rightBeamX, rightBeamY)
  );

  // Beam
  page.drawLine(blackPen, new PdfPoint(leftBeamX, leftBeamY), new PdfPoint(rightBeamX, rightBeamY));
  // Pivot circle
  page.drawEllipse(
    new PdfPen(PdfColor.fromRgb(0, 0, 0), 1),
    PdfBrush.Black,
    new PdfRect(cx - 3, beamY - 3, 6, 6)
  );

  // Pole and base
  const poleBot = rect.top + rect.height - 6;
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(0, 0, 0), 2),
    new PdfPoint(cx, beamY),
    new PdfPoint(cx, poleBot)
  );
  const baseW = rect.width * 0.28;
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(0, 0, 0), 2.5),
    new PdfPoint(cx - baseW / 2, poleBot),
    new PdfPoint(cx + baseW / 2, poleBot)
  );
}

// mountain: triangle silhouette with waypoint markers climbing from base to peak
function drawMountain(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const cx = rect.left + rect.width / 2;
  const baseY = rect.top + rect.height - 8;
  const peakY = rect.top + 10;

  // Mountain polygon: triangle with slight shoulders
  const baseHalfW = rect.width * 0.46;
  const midX1 = cx - rect.width * 0.16;
  const midX2 = cx + rect.width * 0.18;
  const midY = rect.top + rect.height * 0.55;
  const mountainColor = colors[0];
  const mountainPts = [
    new PdfPoint(rect.left + 4, baseY),
    new PdfPoint(cx - baseHalfW, baseY),
    new PdfPoint(midX1, midY),
    new PdfPoint(cx, peakY),
    new PdfPoint(midX2, midY),
    new PdfPoint(cx + baseHalfW, baseY),
    new PdfPoint(rect.left + rect.width - 4, baseY),
  ];
  const snowCapColor = lightenColor(mountainColor, 0.75);
  const snowPts = [
    new PdfPoint(cx, peakY),
    new PdfPoint(cx + rect.width * 0.12, peakY + rect.height * 0.16),
    new PdfPoint(cx - rect.width * 0.12, peakY + rect.height * 0.16),
  ];
  page.drawPolygon(mkPen(mountainColor, 1), mkBrush(mountainColor), mountainPts);
  page.drawPolygon(null, new PdfBrush(PdfColor.fromRgb(240, 245, 255)), snowPts);

  // Path: zigzag from bottom-left up to peak
  const waypointFracs = Array.from({ length: n }, (_, i) => (i + 0.5) / n);
  interface WP {
    x: number;
    y: number;
  }
  const waypoints: WP[] = waypointFracs.map((f, i) => {
    // Interpolate along left slope
    const t = 1 - f; // t=1 at base, t=0 at peak
    const wx =
      cx - (cx - rect.left - 6) * t * 0.7 + (i % 2 === 0 ? -rect.width * 0.06 : rect.width * 0.06);
    const wy = peakY + (baseY - peakY) * t;
    return { x: wx, y: wy };
  });

  // Draw path segments
  for (let i = 0; i < waypoints.length - 1; i++) {
    page.drawLine(
      new PdfPen(PdfColor.fromRgb(255, 255, 255), 1.5),
      new PdfPoint(waypoints[i].x, waypoints[i].y),
      new PdfPoint(waypoints[i + 1].x, waypoints[i + 1].y)
    );
  }

  // Waypoint circles and labels
  const markerR = 7;
  const labelFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  for (let i = 0; i < n; i++) {
    const { x, y } = waypoints[i];
    const col = colors[i % colors.length];
    page.drawEllipse(
      mkPen(col, 1.5),
      new PdfBrush(PdfColor.fromRgb(255, 255, 255)),
      new PdfRect(x - markerR, y - markerR, markerR * 2, markerR * 2)
    );
    const nf = new PdfFont(PdfFont.FONT_NAME, 6);
    page.drawString(
      `${i + 1}`,
      nf,
      mkBrush(col),
      null,
      new PdfRect(x - markerR, y - markerR, markerR * 2, markerR * 2),
      ETextAlign.Center
    );

    // Label offset to the side
    const labelX = i % 2 === 0 ? x - rect.width * 0.38 : x + markerR + 4;
    const labelW = rect.width * 0.3;
    const lf = page.adjustFont(labelW, [nodes[i].text], labelFont);
    page.drawString(
      nodes[i].text,
      lf,
      PdfBrush.Black,
      null,
      new PdfRect(labelX, y - 8, labelW, 16),
      i % 2 === 0 ? ETextAlign.CenterRight : ETextAlign.CenterLeft
    );
  }
}

// fan: semicircular fan with one blade per top-level node; children as sub-arc text
function drawFan(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const cx = rect.left + rect.width / 2;
  // outerR must fit width; also rect.height = outerR + innerR + padding = outerR*(1+0.28)+6
  const outerRByH = (rect.height - 6) / 1.28;
  const outerR = Math.min(rect.width / 2 - 4, outerRByH);
  const innerR = outerR * 0.28;
  // Pivot: low enough that the hub circle (radius innerR) stays inside the rect
  const cy = rect.top + rect.height - innerR - 3;
  const sliceAngle = Math.PI / n;

  for (let i = 0; i < n; i++) {
    const a0 = Math.PI - i * sliceAngle;
    const a1 = Math.PI - (i + 1) * sliceAngle;
    const colorHex = colors[i % colors.length];

    // Draw pie slice via polygon approximation (sin negated → fan opens upward)
    const steps = 12;
    const pts: PdfPoint[] = [new PdfPoint(cx + innerR * Math.cos(a0), cy - innerR * Math.sin(a0))];
    for (let s = 0; s <= steps; s++) {
      const a = a0 + (a1 - a0) * (s / steps);
      pts.push(new PdfPoint(cx + outerR * Math.cos(a), cy - outerR * Math.sin(a)));
    }
    pts.push(new PdfPoint(cx + innerR * Math.cos(a1), cy - innerR * Math.sin(a1)));
    for (let s = steps; s >= 0; s--) {
      const a = a0 + (a1 - a0) * (s / steps);
      pts.push(new PdfPoint(cx + innerR * Math.cos(a), cy - innerR * Math.sin(a)));
    }
    page.drawPolygon(mkPen(colorHex, 0.5), mkBrush(colorHex), pts);

    // Label at midpoint of slice
    const aMid = (a0 + a1) / 2;
    const lDist = (outerR + innerR) / 2;
    const lx = cx + lDist * Math.cos(aMid);
    const ly = cy - lDist * Math.sin(aMid);
    const labelW = (outerR - innerR) * 0.85;
    const labelH = sliceAngle * lDist * 0.7;
    const font = page.adjustFont(labelW, [nodes[i].text], new PdfFont(PdfFont.FONT_NAME, 7));
    page.drawString(
      nodes[i].text,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(lx - labelW / 2, ly - labelH / 2, labelW, labelH),
      ETextAlign.Center
    );
  }

  // Center hub
  page.drawEllipse(
    mkPen(colors[0], 1.5),
    new PdfBrush(lightenColor(colors[0], 0.6)),
    new PdfRect(cx - innerR, cy - innerR, innerR * 2, innerR * 2)
  );
}

// periodic: periodic-table style grid of cells, one per node
function drawPeriodic(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const n = nodes.length;
  const cols = Math.ceil(Math.sqrt(n * (rect.width / rect.height)));
  const rows = Math.ceil(n / cols);
  const gap = 3;
  const cellW = (rect.width - gap * (cols + 1)) / cols;
  const cellH = (rect.height - gap * (rows + 1)) / rows;

  for (let i = 0; i < n; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = rect.left + gap + col * (cellW + gap);
    const cy = rect.top + gap + row * (cellH + gap);
    const colorHex = colors[i % colors.length];

    page.drawRoundedRectangle(
      mkPen(colorHex, 1),
      new PdfBrush(lightenColor(colorHex, 0.55)),
      new PdfRect(cx, cy, cellW, cellH),
      4
    );

    // Number (top-left)
    const numFont = new PdfFont(PdfFont.FONT_NAME, 5.5);
    page.drawString(
      `${i + 1}`,
      numFont,
      mkBrush(colorHex),
      null,
      new PdfRect(cx + 2, cy + 2, cellW - 4, 9),
      ETextAlign.TopLeft
    );

    // Main symbol (center, large)
    const symbols = nodes[i].text.split(/\s+/);
    const symText = symbols[0].substring(0, 2);
    const symFont = page.adjustFont(cellW - 8, [symText], new PdfFont(PdfFont.FONT_NAME, 14));
    page.drawString(
      symText,
      symFont,
      mkBrush(colorHex),
      null,
      new PdfRect(cx, cy + cellH * 0.2, cellW, cellH * 0.45),
      ETextAlign.Center
    );

    // Full name (bottom)
    const nameFont = page.adjustFont(cellW - 4, [nodes[i].text], new PdfFont(PdfFont.FONT_NAME, 5));
    page.drawString(
      nodes[i].text,
      nameFont,
      PdfBrush.Black,
      null,
      new PdfRect(cx + 2, cy + cellH * 0.68, cellW - 4, cellH * 0.28),
      ETextAlign.Center
    );

    // Children as sub-tags
    if (nodes[i].children.length > 0) {
      const tagFont = new PdfFont(PdfFont.FONT_NAME, 4.5);
      page.drawString(
        nodes[i].children.map((c) => c.text).join(', '),
        tagFont,
        PdfBrush.Black,
        null,
        new PdfRect(cx + 2, cy + cellH * 0.9, cellW - 4, cellH * 0.1),
        ETextAlign.Center
      );
    }
  }
}

// cycle-refined: equal-area arc segments arranged as a full circle with inward arrows
function drawCycleRefined(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette
): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const outerR = Math.min(rect.width, rect.height) * 0.45;
  const innerR = outerR * 0.42;
  const gapAngle = 0.06; // radians gap between slices

  for (let i = 0; i < n; i++) {
    const a0 = (2 * Math.PI * i) / n + gapAngle / 2 - Math.PI / 2;
    const a1 = (2 * Math.PI * (i + 1)) / n - gapAngle / 2 - Math.PI / 2;
    const colorHex = colors[i % colors.length];

    // Pie arc polygon
    const steps = 16;
    const pts: PdfPoint[] = [new PdfPoint(cx + innerR * Math.cos(a0), cy + innerR * Math.sin(a0))];
    for (let s = 0; s <= steps; s++) {
      const a = a0 + (a1 - a0) * (s / steps);
      pts.push(new PdfPoint(cx + outerR * Math.cos(a), cy + outerR * Math.sin(a)));
    }
    pts.push(new PdfPoint(cx + innerR * Math.cos(a1), cy + innerR * Math.sin(a1)));
    for (let s = steps; s >= 0; s--) {
      const a = a0 + (a1 - a0) * (s / steps);
      pts.push(new PdfPoint(cx + innerR * Math.cos(a), cy + innerR * Math.sin(a)));
    }
    page.drawPolygon(mkPen(colorHex, 0.5), mkBrush(colorHex), pts);

    // Arrow between this and next segment (at outer rim)
    const aNext0 = (2 * Math.PI * (i + 1)) / n - gapAngle * 0.4 - Math.PI / 2;
    const arrowDist = outerR * 0.88;
    const ax = cx + arrowDist * Math.cos(aNext0);
    const ay = cy + arrowDist * Math.sin(aNext0);
    const perpAngle = aNext0 + Math.PI / 2;
    drawArrowHead(
      page,
      ax - Math.cos(perpAngle) * 3,
      ay - Math.sin(perpAngle) * 3,
      ax,
      ay,
      colorHex,
      5
    );

    // Label at midpoint
    const aMid = (a0 + a1) / 2;
    const lDist = (outerR + innerR) / 2;
    const lx = cx + lDist * Math.cos(aMid);
    const ly = cy + lDist * Math.sin(aMid);
    const arcLen = (a1 - a0) * lDist;
    const radW = (outerR - innerR) * 0.8;
    const font = page.adjustFont(
      Math.min(arcLen * 0.85, radW),
      [nodes[i].text],
      new PdfFont(PdfFont.FONT_NAME, 6.5)
    );
    page.drawString(
      nodes[i].text,
      font,
      paletteTextBrush(palette),
      null,
      new PdfRect(lx - radW / 2, ly - 8, radW, 16),
      ETextAlign.Center
    );

    // Children listed inside the hub if present
    // (handled via hub text below)
  }

  // Center circle
  page.drawEllipse(
    mkPen(colors[0], 1.5),
    new PdfBrush(PdfColor.fromRgb(255, 255, 255)),
    new PdfRect(cx - innerR, cy - innerR, innerR * 2, innerR * 2)
  );

  // If any children exist, list them in hub
  const allChildren = nodes.flatMap((nd) => nd.children.map((c) => c.text));
  if (allChildren.length > 0) {
    const hf = page.adjustFont(innerR * 1.6 - 4, allChildren, new PdfFont(PdfFont.FONT_NAME, 6));
    const lineH = (innerR * 1.8) / allChildren.length;
    for (let i = 0; i < allChildren.length; i++) {
      page.drawString(
        allChildren[i],
        hf,
        PdfBrush.Black,
        null,
        new PdfRect(cx - innerR * 0.9, cy - innerR * 0.9 + i * lineH, innerR * 1.8, lineH),
        ETextAlign.Center
      );
    }
  }
}

// ─── Layout: KWL Chart ────────────────────────────────────────────────────────
// Input: 3 root nodes = K/W/L columns; children = rows in each column
// attrs.headers = "What I Know|What I Want to Know|What I Learned" (default)

function drawKwl(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette,
  attrs: Record<string, string>
): void {
  const defaultHeaders = ['What I Know', 'What I Want to Know', 'What I Learned'];
  const headerLabels = (attrs.headers || 'K|W|L')
    .split('|')
    .map((s, i) => s.trim() || defaultHeaders[i]);
  const n = Math.max(nodes.length, Math.min(headerLabels.length, 3));
  if (n === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const gap = 4;
  const cols = Math.min(n, 3);
  const colW = (rect.width - gap * (cols - 1)) / cols;
  const headerH = Math.min(rect.height * 0.22, 28);
  const bodyH = rect.height - headerH - gap;

  for (let col = 0; col < cols; col++) {
    const node = nodes[col];
    const x = rect.left + col * (colW + gap);
    const colorHex = colors[col % colors.length];
    const label = node ? node.text : headerLabels[col];

    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(x, rect.top, colW, headerH), 3);
    const hf = page.adjustFont(colW - 8, [label], baseFont);
    page.drawString(
      label,
      hf,
      paletteTextBrush(palette),
      null,
      new PdfRect(x + 4, rect.top, colW - 8, headerH),
      ETextAlign.Center
    );

    page.drawRoundedRectangle(
      mkPen(colorHex, 1),
      new PdfBrush(lightenColor(colorHex, 0.88)),
      new PdfRect(x, rect.top + headerH + gap, colW, bodyH),
      3
    );

    const children = node ? node.children : [];
    if (children.length > 0) {
      const rowH = bodyH / children.length;
      for (let i = 0; i < children.length; i++) {
        const ry = rect.top + headerH + gap + i * rowH + 2;
        const cf = page.adjustFont(colW - 16, [children[i].text], childFont);
        page.drawString(
          `· ${children[i].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(x + 8, ry, colW - 16, rowH - 2),
          ETextAlign.CenterLeft
        );
      }
    }
  }
}

// ─── Layout: T-Chart ──────────────────────────────────────────────────────────
// Input: 2 root nodes = left/right columns; children = items in each column

function drawTChart(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette,
  attrs: Record<string, string>
): void {
  if (nodes.length === 0) return;
  const n = Math.min(nodes.length, 2);
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const colW = rect.width / 2;
  const headerH = Math.min(rect.height * 0.22, 28);
  const bodyH = rect.height - headerH;
  const divX = rect.left + colW;

  // Header bar
  page.drawRoundedRectangle(
    null,
    mkBrush(colors[0]),
    new PdfRect(rect.left, rect.top, rect.width, headerH),
    3
  );
  // White divider in header
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(255, 255, 255), 2),
    new PdfPoint(divX, rect.top),
    new PdfPoint(divX, rect.top + headerH)
  );
  // Vertical body divider
  page.drawLine(
    mkPen(colors[0], 1.5),
    new PdfPoint(divX, rect.top + headerH),
    new PdfPoint(divX, rect.top + rect.height)
  );
  // Outer border
  page.drawRoundedRectangle(
    mkPen(colors[0], 1),
    null,
    new PdfRect(rect.left, rect.top, rect.width, rect.height),
    3
  );

  // Optional axis labels from attrs
  const leftLabel = attrs['left-header'] || (nodes[0] ? nodes[0].text : '');
  const rightLabel = attrs['right-header'] || (nodes[1] ? nodes[1].text : '');

  for (let col = 0; col < 2; col++) {
    const node = nodes[col];
    if (!node) continue;
    const x = rect.left + col * colW;
    const colorHex = colors[col % colors.length];
    const label = col === 0 ? leftLabel : rightLabel;

    const hf = page.adjustFont(colW - 10, [label], baseFont);
    page.drawString(
      label,
      hf,
      paletteTextBrush(palette),
      null,
      new PdfRect(x + 5, rect.top, colW - 10, headerH),
      ETextAlign.Center
    );

    if (node.children.length > 0) {
      const rowH = bodyH / node.children.length;
      for (let i = 0; i < node.children.length; i++) {
        const ry = rect.top + headerH + i * rowH + 2;
        const cf = page.adjustFont(colW - 16, [node.children[i].text], childFont);
        page.drawString(
          `• ${node.children[i].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(x + 8, ry, colW - 16, rowH - 2),
          ETextAlign.CenterLeft
        );
      }
    }
  }
}

// ─── Layout: Plus-Minus ───────────────────────────────────────────────────────
// Input: 2 root nodes = pros/cons; children = items in each

function drawPlusMinus(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const gap = 6;
  const colW = (rect.width - gap) / 2;
  const headerH = Math.min(rect.height * 0.22, 28);
  const bodyH = rect.height - headerH - gap;
  const leftNode = nodes[0];
  const rightNode = nodes.length >= 2 ? nodes[1] : null;
  const leftColor = colors[0];
  const rightColor = colors[Math.min(1, colors.length - 1)];

  const drawCol = (node: SmartNode, x: number, colorHex: string, icon: string) => {
    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(x, rect.top, colW, headerH), 4);
    const label = `${icon}  ${node.text}`;
    const hf = page.adjustFont(colW - 12, [label], baseFont);
    page.drawString(
      label,
      hf,
      paletteTextBrush(palette),
      null,
      new PdfRect(x + 6, rect.top, colW - 12, headerH),
      ETextAlign.Center
    );

    page.drawRoundedRectangle(
      mkPen(colorHex, 1),
      new PdfBrush(lightenColor(colorHex, 0.85)),
      new PdfRect(x, rect.top + headerH + gap, colW, bodyH),
      4
    );

    if (node.children.length > 0) {
      const rowH = bodyH / node.children.length;
      for (let i = 0; i < node.children.length; i++) {
        const ry = rect.top + headerH + gap + i * rowH + 2;
        const cf = page.adjustFont(colW - 18, [node.children[i].text], childFont);
        page.drawString(
          `${icon} ${node.children[i].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(x + 8, ry, colW - 16, rowH - 2),
          ETextAlign.CenterLeft
        );
      }
    }
  };

  drawCol(leftNode, rect.left, leftColor, '+');
  if (rightNode) drawCol(rightNode, rect.left + colW + gap, rightColor, '-');
}

// ─── Layout: Frayer Model ─────────────────────────────────────────────────────
// Input: node[0] = center concept, node[1..4] = quadrants (each with children)

function drawFrayer(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const centerNode = nodes[0];
  const quadNodes = nodes.slice(1, 5);
  const gap = 4;
  const halfW = (rect.width - gap) / 2;
  const halfH = (rect.height - gap) / 2;
  const quadDefs = [
    { x: rect.left, y: rect.top, w: halfW, h: halfH },
    { x: rect.left + halfW + gap, y: rect.top, w: halfW, h: halfH },
    { x: rect.left, y: rect.top + halfH + gap, w: halfW, h: halfH },
    { x: rect.left + halfW + gap, y: rect.top + halfH + gap, w: halfW, h: halfH },
  ];

  for (let i = 0; i < 4; i++) {
    const qd = quadDefs[i];
    const node = quadNodes[i];
    if (!node) continue;
    const colorHex = colors[(i + 1) % colors.length];
    page.drawRoundedRectangle(
      mkPen(colorHex, 1.5),
      new PdfBrush(lightenColor(colorHex, 0.88)),
      new PdfRect(qd.x, qd.y, qd.w, qd.h),
      4
    );
    const titleH = Math.min(qd.h * 0.28, 18);
    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(qd.x, qd.y, qd.w, titleH), 4);
    const tf = page.adjustFont(qd.w - 8, [node.text], baseFont);
    page.drawString(
      node.text,
      tf,
      paletteTextBrush(palette),
      null,
      new PdfRect(qd.x + 4, qd.y, qd.w - 8, titleH),
      ETextAlign.Center
    );
    if (node.children.length > 0) {
      const bH = qd.h - titleH - 4;
      const rowH = bH / node.children.length;
      for (let j = 0; j < node.children.length; j++) {
        const cf = page.adjustFont(qd.w - 16, [node.children[j].text], childFont);
        page.drawString(
          `· ${node.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(qd.x + 8, qd.y + titleH + 2 + j * rowH, qd.w - 16, rowH),
          ETextAlign.CenterLeft
        );
      }
    }
  }

  // Center label overlay
  const cr = Math.min(rect.width, rect.height) * 0.14;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  page.drawRoundedRectangle(
    mkPen(colors[0], 2),
    mkBrush(colors[0]),
    new PdfRect(cx - cr, cy - cr * 0.65, cr * 2, cr * 1.3),
    6
  );
  const cf = page.adjustFont(cr * 1.8, [centerNode.text], baseFont);
  page.drawString(
    centerNode.text,
    cf,
    paletteTextBrush(palette),
    null,
    new PdfRect(cx - cr, cy - cr * 0.65, cr * 2, cr * 1.3),
    ETextAlign.Center
  );
}

// ─── Layout: Domino ───────────────────────────────────────────────────────────
// Input: depth-0 items = domino tiles left→right; children = annotation below tile

function drawDomino(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const tileW = Math.min((rect.width - (n - 1) * 6) / n, rect.height * 0.42);
  const tileH = tileW * 2;
  const tileGap = (rect.width - n * tileW) / Math.max(n - 1, 1);
  const annotH = 14;
  const tileTop = rect.top + (rect.height - tileH - annotH - 4) / 2;

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const x = rect.left + i * (tileW + tileGap);
    const tileCx = x + tileW / 2;
    const tileCy = tileTop + tileH / 2;
    const tilt = i < n - 1 ? -0.1 : 0;

    page.save();
    page.rotateAtTransform(tilt, new PdfPoint(tileCx, tileCy));

    page.drawRoundedRectangle(
      mkPen(colorHex, 1),
      mkBrush(colorHex),
      new PdfRect(x, tileTop, tileW, tileH),
      3
    );
    page.drawLine(
      new PdfPen(PdfColor.fromRgb(255, 255, 255), 1.5),
      new PdfPoint(x + 3, tileTop + tileH / 2),
      new PdfPoint(x + tileW - 3, tileTop + tileH / 2)
    );

    const numFont = new PdfFont(PdfFont.FONT_NAME, Math.min(tileW * 0.38, 11));
    page.drawString(
      `${i + 1}`,
      numFont,
      paletteTextBrush(palette),
      null,
      new PdfRect(x, tileTop + 2, tileW, tileH / 2 - 4),
      ETextAlign.Center
    );

    const tf = page.adjustFont(tileW - 6, [node.text], baseFont);
    page.drawString(
      node.text,
      tf,
      paletteTextBrush(palette),
      null,
      new PdfRect(x + 3, tileTop + tileH / 2 + 3, tileW - 6, tileH / 2 - 6),
      ETextAlign.Center
    );

    page.restore();

    if (node.children.length > 0) {
      const af = page.adjustFont(tileW, [node.children[0].text], new PdfFont(PdfFont.FONT_NAME, 7));
      page.drawString(
        node.children[0].text,
        af,
        PdfBrush.Black,
        null,
        new PdfRect(x, tileTop + tileH + 4, tileW, annotH),
        ETextAlign.Center
      );
    }
  }
}

// ─── Layout: Conveyor Belt ────────────────────────────────────────────────────
// Input: depth-0 items = boxes on belt left→right; children = bullet content

function drawConveyor(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);

  const beltH = Math.min(rect.height * 0.16, 16);
  const rollerR = beltH * 0.7;
  const boxH = Math.min(rect.height * 0.48, 60);
  const spacing = rect.width / n;
  const boxW = Math.min(spacing - 8, boxH * 0.9);
  const beltTop = rect.top + rect.height - beltH - rollerR * 2 - 2;
  const boxTop = beltTop - boxH - 4;

  // Belt
  page.drawRoundedRectangle(
    null,
    new PdfBrush(PdfColor.fromRgb(100, 100, 100)),
    new PdfRect(rect.left, beltTop, rect.width, beltH),
    beltH / 2
  );
  // Belt highlight stripe
  page.drawRoundedRectangle(
    null,
    new PdfBrush(PdfColor.fromRgb(140, 140, 140)),
    new PdfRect(rect.left + 4, beltTop + beltH * 0.2, rect.width - 8, beltH * 0.3),
    beltH * 0.15
  );

  // Rollers
  const numRollers = Math.max(Math.floor(rect.width / (rollerR * 3.5)), 2);
  const rollerSpacing = rect.width / numRollers;
  for (let r = 0; r <= numRollers; r++) {
    const rx = rect.left + r * rollerSpacing;
    const ry = beltTop + beltH + rollerR;
    page.drawEllipse(
      null,
      new PdfBrush(PdfColor.fromRgb(80, 80, 80)),
      new PdfRect(rx - rollerR, ry - rollerR, rollerR * 2, rollerR * 2)
    );
    page.drawEllipse(
      null,
      new PdfBrush(PdfColor.fromRgb(140, 140, 140)),
      new PdfRect(rx - rollerR * 0.55, ry - rollerR * 0.55, rollerR * 1.1, rollerR * 1.1)
    );
  }

  // Arrow on belt
  drawArrowHead(
    page,
    rect.left + rect.width - 18,
    beltTop + beltH / 2,
    rect.left + rect.width - 2,
    beltTop + beltH / 2,
    '#FFFFFF',
    6
  );

  // Boxes
  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const bx = rect.left + i * spacing + (spacing - boxW) / 2;

    page.drawRoundedRectangle(
      mkPen(colorHex, 1),
      mkBrush(colorHex),
      new PdfRect(bx, boxTop, boxW, boxH),
      3
    );

    if (node.children.length === 0) {
      const tf = page.adjustFont(boxW - 8, [node.text], baseFont);
      page.drawString(
        node.text,
        tf,
        paletteTextBrush(palette),
        null,
        new PdfRect(bx + 4, boxTop + 4, boxW - 8, boxH - 8),
        ETextAlign.Center
      );
    } else {
      const labelH = Math.min(boxH * 0.35, 18);
      const tf = page.adjustFont(boxW - 8, [node.text], baseFont);
      page.drawString(
        node.text,
        tf,
        paletteTextBrush(palette),
        null,
        new PdfRect(bx + 4, boxTop + 2, boxW - 8, labelH),
        ETextAlign.Center
      );
      const lightFill = new PdfBrush(lightenColor(colorHex, 0.65));
      page.drawRoundedRectangle(
        null,
        lightFill,
        new PdfRect(bx, boxTop + labelH + 2, boxW, boxH - labelH - 2),
        3
      );
      const childH = boxH - labelH - 4;
      const rowH = childH / node.children.length;
      for (let j = 0; j < node.children.length; j++) {
        const cf = page.adjustFont(
          boxW - 10,
          [node.children[j].text],
          new PdfFont(PdfFont.FONT_NAME, 6)
        );
        page.drawString(
          `· ${node.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(bx + 5, boxTop + labelH + 2 + j * rowH, boxW - 10, rowH),
          ETextAlign.CenterLeft
        );
      }
    }
  }
}

// ─── Layout: Solar System ─────────────────────────────────────────────────────
// Input: node[0] = sun; remaining depth-0 = planets; planet children = moons

function drawSolarSystem(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 7.5);
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const sunNode = nodes[0];
  const planets = nodes.slice(1);
  const np = planets.length;
  const sunR = Math.min(rect.width, rect.height) * 0.11;
  const maxOrbit = Math.min(rect.width, rect.height) / 2 - 8;
  const orbitPen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);

  // Sun
  page.drawEllipse(
    mkPen(colors[0], 1.5),
    mkBrush(colors[0]),
    new PdfRect(cx - sunR, cy - sunR, sunR * 2, sunR * 2)
  );
  const sf = page.adjustFont(sunR * 1.8, [sunNode.text], baseFont);
  page.drawString(
    sunNode.text,
    sf,
    paletteTextBrush(palette),
    null,
    new PdfRect(cx - sunR, cy - sunR, sunR * 2, sunR * 2),
    ETextAlign.Center
  );

  for (let i = 0; i < np; i++) {
    const planet = planets[i];
    const orbitFrac = (i + 1) / (np + 0.5);
    const orbitRx = (maxOrbit - sunR) * orbitFrac + sunR;
    const orbitRy = orbitRx * 0.52;

    page.drawEllipse(
      orbitPen,
      null,
      new PdfRect(cx - orbitRx, cy - orbitRy, orbitRx * 2, orbitRy * 2)
    );

    const angle = (2 * Math.PI * i) / np - Math.PI / 2;
    const px = cx + orbitRx * Math.cos(angle);
    const py = cy + orbitRy * Math.sin(angle);
    const planetR = Math.max(sunR * 0.32, 7);
    const planetColor = colors[(i + 1) % colors.length];

    page.drawEllipse(
      null,
      mkBrush(planetColor),
      new PdfRect(px - planetR, py - planetR, planetR * 2, planetR * 2)
    );
    const pf = page.adjustFont(planetR * 1.8, [planet.text], baseFont);
    page.drawString(
      planet.text,
      pf,
      paletteTextBrush(palette),
      null,
      new PdfRect(px - planetR, py - planetR, planetR * 2, planetR * 2),
      ETextAlign.Center
    );

    for (let j = 0; j < planet.children.length; j++) {
      const moonAngle = angle + 0.7 + j * 1.3;
      const moonDist = planetR + 9;
      const mx = px + moonDist * Math.cos(moonAngle);
      const my = py + moonDist * Math.sin(moonAngle);
      const moonR = Math.max(planetR * 0.38, 4);
      const moonColor = colors[(i + 2) % colors.length];
      page.drawLine(
        new PdfPen(PdfColor.fromHex(planetColor), 0.5),
        new PdfPoint(px, py),
        new PdfPoint(mx, my)
      );
      page.drawEllipse(
        null,
        new PdfBrush(lightenColor(moonColor, 0.45)),
        new PdfRect(mx - moonR, my - moonR, moonR * 2, moonR * 2)
      );
      const mf = page.adjustFont(
        moonR * 1.7,
        [planet.children[j].text],
        new PdfFont(PdfFont.FONT_NAME, 5.5)
      );
      page.drawString(
        planet.children[j].text,
        mf,
        PdfBrush.Black,
        null,
        new PdfRect(mx - moonR, my - moonR, moonR * 2, moonR * 2),
        ETextAlign.Center
      );
    }
  }
}

// ─── Layout: Bridge ───────────────────────────────────────────────────────────
// Input: depth-0 items = main sequence; children[0] = bridging label to next node

function drawBridge(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const mainH = Math.min(rect.height * 0.42, 48);
  const connH = Math.min(rect.height * 0.22, 24);
  const mainY = rect.top + (rect.height - mainH) / 2;
  const connY = mainY - connH - 4;
  const mainW = Math.min((rect.width - (n - 1) * 8) / n, 70);
  const spacing = (rect.width - n * mainW) / Math.max(n - 1, 1);

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const x = rect.left + i * (mainW + spacing);

    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(x, mainY, mainW, mainH), 4);
    const tf = page.adjustFont(mainW - 8, [node.text], baseFont);
    page.drawString(
      node.text,
      tf,
      paletteTextBrush(palette),
      null,
      new PdfRect(x + 4, mainY, mainW - 8, mainH),
      ETextAlign.Center
    );

    if (i < n - 1) {
      const nextX = rect.left + (i + 1) * (mainW + spacing);
      const nextColor = colors[(i + 1) % colors.length];
      const arrowMidY = mainY + mainH / 2;
      page.drawLine(
        mkPen(nextColor, 1.5),
        new PdfPoint(x + mainW + 2, arrowMidY),
        new PdfPoint(nextX - 2, arrowMidY)
      );
      drawArrowHead(page, x + mainW, arrowMidY, nextX - 1, arrowMidY, nextColor, 6);

      if (node.children.length > 0) {
        const bridgeMidX = x + mainW + spacing / 2;
        const bridgeW = Math.min(spacing - 6, 58);
        const bx = bridgeMidX - bridgeW / 2;
        page.drawRoundedRectangle(
          mkPen(nextColor, 1),
          new PdfBrush(lightenColor(nextColor, 0.78)),
          new PdfRect(bx, connY, bridgeW, connH),
          3
        );
        const bf = page.adjustFont(bridgeW - 8, [node.children[0].text], childFont);
        page.drawString(
          node.children[0].text,
          bf,
          PdfBrush.Black,
          null,
          new PdfRect(bx + 4, connY, bridgeW - 8, connH),
          ETextAlign.Center
        );
        page.drawLine(
          mkPen(nextColor, 0.75),
          new PdfPoint(bridgeMidX, connY + connH),
          new PdfPoint(bridgeMidX, arrowMidY)
        );
      }
    }
  }
}

// ─── Layout: Nested Target ────────────────────────────────────────────────────
// Input: depth-0 items = layers outside→inside; like target but with rect rings

function drawNestedTarget(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette
): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  for (let i = 0; i < n; i++) {
    const frac = (n - i) / n;
    const w = rect.width * frac;
    const h = rect.height * frac;
    const colorHex = colors[i % colors.length];
    page.drawRoundedRectangle(
      null,
      mkBrush(colorHex),
      new PdfRect(cx - w / 2, cy - h / 2, w, h),
      4
    );
  }

  // White separators and labels
  const whitePen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 1);
  for (let i = 0; i < n; i++) {
    const frac = (n - i) / n;
    const innerFrac = i < n - 1 ? (n - i - 1) / n : 0;
    const w = rect.width * frac;
    const h = rect.height * frac;
    const iw = rect.width * innerFrac;
    const colorHex = colors[i % colors.length];
    page.drawRoundedRectangle(whitePen, null, new PdfRect(cx - w / 2, cy - h / 2, w, h), 4);
    const ringW = (w - iw) / 2 - 6;
    if (ringW > 12) {
      const lx = cx - w / 2 + 4;
      const lh = Math.min(h * 0.45, 14);
      const font = page.adjustFont(ringW, [nodes[i].text], baseFont);
      page.drawString(
        nodes[i].text,
        font,
        paletteTextBrush(palette),
        null,
        new PdfRect(lx, cy - lh / 2, ringW, lh),
        ETextAlign.CenterLeft
      );
    }
  }
}

// ─── Layout: Gear ─────────────────────────────────────────────────────────────
// Input: up to 3 depth-0 items = gear labels; children = detail text

function drawGear(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = Math.min(nodes.length, 3);
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 6.5);

  const gearR = Math.min(rect.width / 4.2, rect.height / 3.2, 38);
  const toothH = gearR * 0.2;
  const numTeeth = 10;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const gearCenters =
    n === 1
      ? [{ x: cx, y: cy }]
      : n === 2
        ? [
            { x: cx - gearR * 1.08, y: cy },
            { x: cx + gearR * 1.08, y: cy },
          ]
        : [
            { x: cx - gearR * 1.12, y: cy - gearR * 0.38 },
            { x: cx + gearR * 1.12, y: cy - gearR * 0.38 },
            { x: cx, y: cy + gearR * 1.08 },
          ];

  // Cluster center used to compute outward direction for children text
  const clusterCx = gearCenters.reduce((s, g) => s + g.x, 0) / gearCenters.length;
  const clusterCy = gearCenters.reduce((s, g) => s + g.y, 0) / gearCenters.length;

  const drawGearShape = (
    gcx: number,
    gcy: number,
    colorHex: string,
    label: string,
    children: SmartNode[]
  ) => {
    const pts: PdfPoint[] = [];
    for (let t = 0; t < numTeeth; t++) {
      const a0 = (2 * Math.PI * t) / numTeeth;
      const a1 = (2 * Math.PI * (t + 0.5)) / numTeeth;
      const a2 = (2 * Math.PI * (t + 1)) / numTeeth;
      const da = (Math.PI / numTeeth) * 0.25;
      pts.push(new PdfPoint(gcx + gearR * Math.cos(a0 + da), gcy + gearR * Math.sin(a0 + da)));
      pts.push(
        new PdfPoint(
          gcx + (gearR + toothH) * Math.cos(a1 - da),
          gcy + (gearR + toothH) * Math.sin(a1 - da)
        )
      );
      pts.push(
        new PdfPoint(
          gcx + (gearR + toothH) * Math.cos(a1 + da),
          gcy + (gearR + toothH) * Math.sin(a1 + da)
        )
      );
      pts.push(new PdfPoint(gcx + gearR * Math.cos(a2 - da), gcy + gearR * Math.sin(a2 - da)));
    }
    page.drawPolygon(mkPen(colorHex, 1), mkBrush(colorHex), pts);

    const holeR = gearR * 0.32;
    page.drawEllipse(
      null,
      new PdfBrush(lightenColor(colorHex, 0.72)),
      new PdfRect(gcx - holeR, gcy - holeR, holeR * 2, holeR * 2)
    );

    const lf = page.adjustFont(gearR * 1.55, [label], baseFont);
    page.drawString(
      label,
      lf,
      PdfBrush.Black,
      null,
      new PdfRect(gcx - gearR * 0.75, gcy - gearR * 0.35, gearR * 1.5, gearR * 0.7),
      ETextAlign.Center
    );

    // Children text: stack radially outward from cluster center to avoid overlapping gears
    if (children.length > 0) {
      const dx = gcx - clusterCx;
      const dy = gcy - clusterCy;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const outX = dx / dist;
      const outY = dy / dist;
      const childW = gearR * 1.9;
      const rowH = 10;
      const nc = Math.min(children.length, 2);
      for (let j = 0; j < nc; j++) {
        const radialDist = gearR + toothH + 4 + j * (rowH + 2);
        const tx = gcx + radialDist * outX - childW / 2;
        const ty = gcy + radialDist * outY - rowH / 2;
        const cf = page.adjustFont(childW, [children[j].text], childFont);
        page.drawString(
          children[j].text,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(tx, ty, childW, rowH),
          ETextAlign.Center
        );
      }
    }
  };

  for (let i = 0; i < n; i++) {
    const gc = gearCenters[i];
    drawGearShape(gc.x, gc.y, colors[i % colors.length], nodes[i].text, nodes[i].children);
  }
}

// ─── Layout: Roadmap ──────────────────────────────────────────────────────────
// Input: depth-0 items = milestones in order; children = detail bullets

function drawRoadmap(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);

  const trackH = 8;
  const dotR = 7;
  const cardH = Math.min((rect.height - trackH - dotR * 2 - 14) / 2, 52);
  const trackY = rect.top + rect.height * 0.5;
  const spacing = rect.width / n;

  // Track background
  page.drawRoundedRectangle(
    null,
    new PdfBrush(PdfColor.fromRgb(220, 220, 220)),
    new PdfRect(rect.left, trackY - trackH / 2, rect.width, trackH),
    trackH / 2
  );
  // Progress fill (first half colored)
  if (n > 0) {
    page.drawRoundedRectangle(
      null,
      mkBrush(colors[0]),
      new PdfRect(rect.left, trackY - trackH / 2, rect.width * 0.5, trackH),
      trackH / 2
    );
  }

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const mx = rect.left + i * spacing + spacing / 2;
    const isAbove = i % 2 === 0;
    const cardW = Math.min(spacing - 4, 78);
    const bx = mx - cardW / 2;
    const cardY = isAbove ? trackY - dotR - cardH - 6 : trackY + dotR + 6;

    // Milestone dot
    page.drawEllipse(
      null,
      mkBrush(colorHex),
      new PdfRect(mx - dotR, trackY - dotR, dotR * 2, dotR * 2)
    );
    const numFont = new PdfFont(PdfFont.FONT_NAME, Math.min(dotR * 0.9, 7));
    page.drawString(
      `${i + 1}`,
      numFont,
      paletteTextBrush(palette),
      null,
      new PdfRect(mx - dotR, trackY - dotR, dotR * 2, dotR * 2),
      ETextAlign.Center
    );

    // Connector
    const cy2 = isAbove ? trackY - dotR : trackY + dotR;
    const cy3 = isAbove ? cardY + cardH : cardY;
    page.drawLine(mkPen(colorHex, 1), new PdfPoint(mx, cy2), new PdfPoint(mx, cy3));

    // Card
    page.drawRoundedRectangle(null, mkBrush(colorHex), new PdfRect(bx, cardY, cardW, cardH), 3);
    const titleH = node.children.length > 0 ? Math.min(cardH * 0.38, 16) : cardH;
    const tf = page.adjustFont(cardW - 8, [node.text], baseFont);
    page.drawString(
      node.text,
      tf,
      paletteTextBrush(palette),
      null,
      new PdfRect(bx + 4, cardY, cardW - 8, titleH),
      ETextAlign.Center
    );

    if (node.children.length > 0) {
      const bH = cardH - titleH - 2;
      const rowH = bH / node.children.length;
      page.drawRoundedRectangle(
        null,
        new PdfBrush(lightenColor(colorHex, 0.75)),
        new PdfRect(bx, cardY + titleH, cardW, bH),
        3
      );
      for (let j = 0; j < node.children.length; j++) {
        const cf = page.adjustFont(cardW - 14, [node.children[j].text], childFont);
        page.drawString(
          `· ${node.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(bx + 7, cardY + titleH + j * rowH + 1, cardW - 14, rowH - 1),
          ETextAlign.CenterLeft
        );
      }
    }
  }
}

// ─── Layout: Waffle Chart ─────────────────────────────────────────────────────
// Input: depth-0 items = segments; text format "Label:value" (value = proportion)

function drawWaffle(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const gridSize = 10;
  const gap = 1;
  const legendH = Math.min(rect.height * 0.18, 26);
  const gridAreaH = rect.height - legendH - 4;
  const cellSize = Math.min(
    (rect.width - gap * (gridSize - 1)) / gridSize,
    (gridAreaH - gap * (gridSize - 1)) / gridSize
  );
  const gridW = cellSize * gridSize + gap * (gridSize - 1);
  const gridLeft = rect.left + (rect.width - gridW) / 2;
  const gridTop = rect.top;

  interface WaffleSeg {
    label: string;
    value: number;
    colorHex: string;
  }
  const segs: WaffleSeg[] = [];
  let total = 0;
  for (let i = 0; i < nodes.length; i++) {
    const t = nodes[i].text;
    const ci = t.lastIndexOf(':');
    const label = ci !== -1 ? t.slice(0, ci).trim() : t;
    const value = ci !== -1 ? parseFloat(t.slice(ci + 1)) || 10 : 10;
    total += value;
    segs.push({ label, value, colorHex: colors[i % colors.length] });
  }

  // Build cell color array (100 cells, bottom-left origin)
  const cells: string[] = new Array(100).fill('#DDDDDD');
  let c = 0;
  for (const seg of segs) {
    const count = Math.round((seg.value / total) * 100);
    for (let k = 0; k < count && c < 100; k++, c++) cells[c] = seg.colorHex;
  }

  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const idx = (gridSize - 1 - row) * gridSize + col;
      const gx = gridLeft + col * (cellSize + gap);
      const gy = gridTop + row * (cellSize + gap);
      page.drawRoundedRectangle(
        null,
        mkBrush(cells[idx] || '#DDDDDD'),
        new PdfRect(gx, gy, cellSize, cellSize),
        1
      );
    }
  }

  // Legend
  const legendY = gridTop + gridAreaH + 4;
  const lw = rect.width / segs.length;
  const dotSz = 8;
  for (let i = 0; i < segs.length; i++) {
    const lx = rect.left + i * lw;
    const pct = Math.round((segs[i].value / total) * 100);
    page.drawRoundedRectangle(
      null,
      mkBrush(segs[i].colorHex),
      new PdfRect(lx, legendY + (legendH - dotSz) / 2, dotSz, dotSz),
      1
    );
    const lf = page.adjustFont(
      lw - dotSz - 4,
      [`${segs[i].label} ${pct}%`],
      new PdfFont(PdfFont.FONT_NAME, 7)
    );
    page.drawString(
      `${segs[i].label} ${pct}%`,
      lf,
      PdfBrush.Black,
      null,
      new PdfRect(lx + dotSz + 2, legendY, lw - dotSz - 2, legendH),
      ETextAlign.CenterLeft
    );
  }
}

// ─── Layout: Stacked Blocks ───────────────────────────────────────────────────
// Input: depth-0 items = blocks top→bottom; children = pill tags inside block

function drawStackedBlocks(
  page: PageGen,
  rect: PdfRect,
  nodes: SmartNode[],
  palette: Palette
): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 9);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const gap = 2;
  const connH = 10;
  const blockH = (rect.height - connH * (n - 1) - gap * (n - 1)) / n;

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const y = rect.top + i * (blockH + gap + connH);

    page.drawRoundedRectangle(
      null,
      mkBrush(colorHex),
      new PdfRect(rect.left, y, rect.width, blockH),
      4
    );

    if (node.children.length === 0) {
      const tf = page.adjustFont(rect.width - 12, [node.text], baseFont);
      page.drawString(
        node.text,
        tf,
        paletteTextBrush(palette),
        null,
        new PdfRect(rect.left + 6, y, rect.width - 12, blockH),
        ETextAlign.Center
      );
    } else {
      const labelW = rect.width * 0.28;
      const tf = page.adjustFont(labelW - 8, [node.text], baseFont);
      page.drawString(
        node.text,
        tf,
        paletteTextBrush(palette),
        null,
        new PdfRect(rect.left + 6, y, labelW - 4, blockH),
        ETextAlign.CenterLeft
      );
      const pillCount = node.children.length;
      const pillArea = rect.width - labelW - 8;
      const pillW = (pillArea - (pillCount - 1) * 4) / pillCount;
      const pillH = Math.min(blockH - 8, 18);
      const pillY = y + (blockH - pillH) / 2;
      for (let j = 0; j < pillCount; j++) {
        const px = rect.left + labelW + j * (pillW + 4);
        page.drawRoundedRectangle(
          null,
          new PdfBrush(lightenColor(colorHex, 0.62)),
          new PdfRect(px, pillY, pillW, pillH),
          pillH / 2
        );
        const cf = page.adjustFont(pillW - 6, [node.children[j].text], childFont);
        page.drawString(
          node.children[j].text,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(px + 3, pillY, pillW - 6, pillH),
          ETextAlign.Center
        );
      }
    }

    if (i < n - 1) {
      const nextColor = colors[(i + 1) % colors.length];
      const connX = rect.left + rect.width * 0.35;
      const connW = rect.width * 0.3;
      const cy2 = y + blockH + gap;
      page.drawRoundedRectangle(null, mkBrush(nextColor), new PdfRect(connX, cy2, connW, connH), 2);
      drawArrowHead(page, connX + connW / 2, cy2, connX + connW / 2, cy2 + connH, nextColor, 5);
    }
  }
}

// ─── Layout: Linear Venn ──────────────────────────────────────────────────────
// Input: depth-0 items = sets in a left→right sequence; children = unique items

function drawLinearVenn(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const n = nodes.length;
  const {colors} = palette;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 8);
  const childFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const overlap = 0.28;
  const r = Math.min(rect.width / (n * 2 * (1 - overlap) + overlap * 2), rect.height / 2 - 4);
  const step = r * 2 * (1 - overlap);
  const totalW = r * 2 + step * (n - 1);
  const startCx = rect.left + (rect.width - totalW) / 2 + r;
  const cy = rect.top + rect.height / 2;

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    const colorHex = colors[i % colors.length];
    const cx2 = startCx + i * step;

    page.drawEllipse(
      null,
      new PdfBrush(lightenColor(colorHex, 0.62)),
      new PdfRect(cx2 - r, cy - r, r * 2, r * 2)
    );
    page.drawEllipse(mkPen(colorHex, 1.5), null, new PdfRect(cx2 - r, cy - r, r * 2, r * 2));

    const exclusiveX =
      i === 0 ? cx2 - r + 4 : i === n - 1 ? cx2 + r * overlap - 4 - r * 0.9 : cx2 - r * 0.35;
    const exclusiveW = r * 0.85;
    const tf = page.adjustFont(exclusiveW, [node.text], baseFont);
    page.drawString(
      node.text,
      tf,
      mkBrush(colorHex),
      null,
      new PdfRect(exclusiveX, cy - r + 4, exclusiveW, 14),
      ETextAlign.Center
    );

    if (node.children.length > 0) {
      const rowH = Math.min((r * 1.4) / node.children.length, 12);
      for (let j = 0; j < node.children.length; j++) {
        const cf = page.adjustFont(exclusiveW, [node.children[j].text], childFont);
        page.drawString(
          `· ${node.children[j].text}`,
          cf,
          PdfBrush.Black,
          null,
          new PdfRect(exclusiveX, cy - r * 0.35 + j * rowH, exclusiveW, rowH),
          ETextAlign.CenterLeft
        );
      }
    }
  }
}

// spider-map: center node with radial arms, each arm has branch nodes and sub-branches
function drawSpiderMap(page: PageGen, rect: PdfRect, nodes: SmartNode[], palette: Palette): void {
  if (nodes.length === 0) return;
  const {colors} = palette;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const centerR = Math.min(rect.width * 0.12, rect.height * 0.14, 24);
  const armLen = Math.min(rect.width, rect.height) * 0.27;
  const nodeR = Math.min(armLen * 0.18, 13);
  const subArmLen = armLen * 0.42;
  const subR = nodeR * 0.7;
  const n = nodes.length;
  const baseFont = new PdfFont(PdfFont.FONT_NAME, 6.5);
  const subFont = new PdfFont(PdfFont.FONT_NAME, 5.5);

  // Center
  const centerColor = colors[0];
  page.drawEllipse(
    mkPen(centerColor, 1.5),
    mkBrush(centerColor),
    new PdfRect(cx - centerR, cy - centerR, centerR * 2, centerR * 2)
  );

  // Determine center label from first node if it looks like a root, otherwise use 'Center'
  const centerText = nodes[0].children.length > 0 ? nodes[0].text : '';
  if (centerText) {
    const cf = page.adjustFont(centerR * 1.8, [centerText], new PdfFont(PdfFont.FONT_NAME, 7));
    page.drawString(
      centerText,
      cf,
      paletteTextBrush(palette),
      null,
      new PdfRect(cx - centerR, cy - centerR, centerR * 2, centerR * 2),
      ETextAlign.Center
    );
  }

  // Arms
  const arms = nodes[0].children.length > 0 ? nodes[0].children : nodes;
  const na = arms.length;
  for (let i = 0; i < na; i++) {
    const angle = (2 * Math.PI * i) / na - Math.PI / 2;
    const colorHex = colors[(i + 1) % colors.length];
    const ax = cx + armLen * Math.cos(angle);
    const ay = cy + armLen * Math.sin(angle);

    // Arm line from center
    page.drawLine(
      mkPen(colorHex, 1.8),
      new PdfPoint(cx + centerR * Math.cos(angle), cy + centerR * Math.sin(angle)),
      new PdfPoint(ax - nodeR * Math.cos(angle), ay - nodeR * Math.sin(angle))
    );

    // Arm node
    page.drawEllipse(
      mkPen(colorHex, 1.2),
      mkBrush(colorHex),
      new PdfRect(ax - nodeR, ay - nodeR, nodeR * 2, nodeR * 2)
    );
    const af = page.adjustFont(nodeR * 1.8, [arms[i].text], baseFont);
    page.drawString(
      arms[i].text,
      af,
      paletteTextBrush(palette),
      null,
      new PdfRect(ax - nodeR, ay - nodeR, nodeR * 2, nodeR * 2),
      ETextAlign.Center
    );

    // Sub-branches
    const subs = arms[i].children;
    const ns = subs.length;
    for (let j = 0; j < ns; j++) {
      const subSpread = Math.PI * 0.55;
      const subAngle = angle - subSpread / 2 + (subSpread / Math.max(ns - 1, 1)) * j;
      const sx = ax + subArmLen * Math.cos(subAngle);
      const sy = ay + subArmLen * Math.sin(subAngle);

      page.drawLine(
        mkPen(colorHex, 0.8),
        new PdfPoint(ax + nodeR * Math.cos(subAngle), ay + nodeR * Math.sin(subAngle)),
        new PdfPoint(sx - subR * Math.cos(subAngle), sy - subR * Math.sin(subAngle))
      );
      page.drawEllipse(
        mkPen(colorHex, 1),
        new PdfBrush(PdfColor.fromRgb(255, 255, 255)),
        new PdfRect(sx - subR, sy - subR, subR * 2, subR * 2)
      );

      // Label outside the sub-node
      const labelDist = subR + 3;
      const lx = sx + labelDist * Math.cos(subAngle);
      const ly = sy + labelDist * Math.sin(subAngle);
      const lw = subArmLen * 0.7;
      const sf = page.adjustFont(lw, [subs[j].text], subFont);
      const align = Math.cos(subAngle) > 0 ? ETextAlign.CenterLeft : ETextAlign.CenterRight;
      const lxAdj = Math.cos(subAngle) > 0 ? lx : lx - lw;
      page.drawString(
        subs[j].text,
        sf,
        PdfBrush.Black,
        null,
        new PdfRect(lxAdj, ly - 6, lw, 12),
        align
      );
    }
  }
}
